import { useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, TouchEvent as ReactTouchEvent } from 'react';
import Map, { MapMarker } from '../components/Map';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CustomComboBox } from '../components/CustomComboBox';
import { VenueNameComboBox } from '../components/VenueNameComboBox';
import { BulkCSVUpload } from '../components/BulkCSVUpload';
import { Airport, Machine, MachineTier } from '../types/map';
import { getAirports, getAirportMachines, getMachines, createMachine, createMachineMedia, updateMachine, deleteMachine, deleteMachineMedia, getPlanograms, createPlanogram, Planogram, getMachineTiers, createMachineTier } from '../lib/api';
import { centsFromPounds, formatCurrency } from '../lib/utils';
import { Pencil, Move, Trash2, X, Layers, Search, MapPin, ChevronDown } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { MAP_STYLES, MapStyle, DEFAULT_MAP_STYLE } from '../lib/mapConfig';

const airportViewOverrides: Record<
  string,
  {
    center: [number, number];
    zoom: number;
  }
> = {
  EMA: {
    center: [52.8288, -1.3288],
    zoom: 16,
  },
};

const DEFAULT_ZOOM_OFFSET = 3;

export default function AdminMap() {
  const [airports, setAirports] = useState<Airport[]>([]);
  const [selectedAirport, setSelectedAirport] = useState<Airport | null>(null);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [dropPinMode, setDropPinMode] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [uploadMode, setUploadMode] = useState<'manual' | 'csv'>('manual');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [newMachine, setNewMachine] = useState({
    lat: 0,
    lng: 0,
    name: '',
    description: '',
    cost: '',
    label: '',
    pinColor: '#3B82F6',
    isActive: true,
    status: 'Live/Available' as 'Live/Available' | 'Reserved' | 'Coming Soon',
    availabilityType: '' as string,
    availableSkus: '' as string | number,
    areaImageUrl: '',
    renderImageUrl: '',
    venueType: '',
    venueName: '',
    machineType: '',
    resolution: '',
    location: '',
    capacity: '',
    format: '',
    screenSize: '',
    playbackFormat: '',
    duration: '',
    orientation: '',
    aspectRatio: '',
    addressLine1: '',
    addressLine2: '',
    postCode: '',
    county: '',
    city: '',
    requiresTenderProcess: false,
    planogramConfig: '',
    planogramId: '' as string,
    tenderTimeframe: '',
    tenderLaunchDate: '',
    tenderType: '' as 'long-term' | 'short-term' | '',
    minimumMachines: '' as string | number,
    machineTierId: '' as string,
  });
  const [areaImageFile, setAreaImageFile] = useState<File | null>(null);
  const [renderImageFile, setRenderImageFile] = useState<File | null>(null);
  const [uploadingAreaImage, setUploadingAreaImage] = useState(false);
  const [uploadingRenderImage, setUploadingRenderImage] = useState(false);
  const [editAreaImageFile, setEditAreaImageFile] = useState<File | null>(null);
  const [editRenderImageFile, setEditRenderImageFile] = useState<File | null>(null);
  const [removeAreaImage, setRemoveAreaImage] = useState(false);
  const [removeRenderImage, setRemoveRenderImage] = useState(false);
  const [uploadingEditAreaImage, setUploadingEditAreaImage] = useState(false);
  const [uploadingEditRenderImage, setUploadingEditRenderImage] = useState(false);

  // Edit/Delete state
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
  const [selectedMachines, setSelectedMachines] = useState<Machine[]>([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showBulkEditDialog, setShowBulkEditDialog] = useState(false);
  const [moveMode, setMoveMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    cost: '',
    label: '',
    pinColor: '#3B82F6',
    isActive: true,
    status: 'Live/Available' as 'Live/Available' | 'Reserved' | 'Coming Soon' | '',
    availabilityType: '' as string,
    availableSkus: '' as string | number,
    areaImageUrl: '',
    renderImageUrl: '',
    venueType: '',
    venueName: '',
    machineType: '',
    resolution: '',
    location: '',
    capacity: '',
    format: '',
    screenSize: '',
    playbackFormat: '',
    duration: '',
    orientation: '',
    aspectRatio: '',
    addressLine1: '',
    addressLine2: '',
    postCode: '',
    county: '',
    city: '',
    requiresTenderProcess: false,
    planogramConfig: '',
    planogramId: '' as string,
    tenderTimeframe: '',
    tenderLaunchDate: '',
    tenderType: '' as 'long-term' | 'short-term' | '',
    minimumMachines: '' as string | number,
    machineTierId: '' as string,
    footfall: '' as string | number,
    priceFromPer2Weeks: '',
  });

  const [mapCenter, setMapCenter] = useState<[number, number]>([54.0, -2.5]);
  const [mapZoom, setMapZoom] = useState(6);
  const [mapStyle, setMapStyle] = useState<MapStyle>(DEFAULT_MAP_STYLE);
  const [showPlanogramModal, setShowPlanogramModal] = useState(false);
  const [availablePlanograms, setAvailablePlanograms] = useState<Planogram[]>([]);
  const [loadingPlanograms, setLoadingPlanograms] = useState(false);
  const [editingPlanogram, setEditingPlanogram] = useState<'create' | 'edit'>('create');
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  const [categoryInput, setCategoryInput] = useState('');
  const [planogramName, setPlanogramName] = useState('');
  const [availableTiers, setAvailableTiers] = useState<MachineTier[]>([]);
  const [loadingTiers, setLoadingTiers] = useState(false);
  const [showCreateTierDialog, setShowCreateTierDialog] = useState(false);
  const [showBulkCreateTierDialog, setShowBulkCreateTierDialog] = useState(false);
  const [showBulkCreatePlanogramDialog, setShowBulkCreatePlanogramDialog] = useState(false);
  const [isBulkPlanogramMode, setIsBulkPlanogramMode] = useState(false);
  const [newTierName, setNewTierName] = useState('');
  const [newTierPlanogramId, setNewTierPlanogramId] = useState<string>('');
  const [bulkTierName, setBulkTierName] = useState('');
  const [bulkTierPlanogramId, setBulkTierPlanogramId] = useState<string>('');
  
  // Category management with colors - stored in localStorage
  const [savedCategories, setSavedCategories] = useState<Record<string, string>>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('planogramCategories');
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch (e) {
          // Default categories with colors
          return {
            'Energy Drinks': '#FEF3C7',
            'Snacks': '#FED7AA',
            'Beverages': '#DBEAFE',
            'Healthy Options': '#D1FAE5',
            'Confectionery': '#FCE7F3',
          };
        }
      }
      // Default categories with colors
      return {
        'Energy Drinks': '#FEF3C7',
        'Snacks': '#FED7AA',
        'Beverages': '#DBEAFE',
        'Healthy Options': '#D1FAE5',
        'Confectionery': '#FCE7F3',
      };
    }
    return {};
  });
  const [selectedCategoryColor, setSelectedCategoryColor] = useState('#FEF3C7');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  // Sync color when category is selected
  useEffect(() => {
    if (categoryInput && savedCategories[categoryInput]) {
      setSelectedCategoryColor(savedCategories[categoryInput]);
    }
  }, [categoryInput, savedCategories]);
  const [mapHeight, setMapHeight] = useState(() => {
    if (typeof window === 'undefined') {
      return 600;
    }
    return Math.max(window.innerHeight * 0.6, 400);
  });
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartYRef = useRef(0);
  const resizeStartHeightRef = useRef(mapHeight);
  const [expandedVenueTypes, setExpandedVenueTypes] = useState<Set<string>>(new Set());
  const [expandedSubcategories, setExpandedSubcategories] = useState<Set<string>>(new Set());
  const [availabilityTypeOptions, setAvailabilityTypeOptions] = useState<string[]>([]);

  useEffect(() => {
    loadAvailabilityTypes();
  }, []);

  async function loadAvailabilityTypes() {
    try {
      const res = await fetch('/api/admin/field-options?fieldName=availabilityType', {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setAvailabilityTypeOptions(data.map((opt: any) => opt.value));
      }
    } catch (error) {
      console.error('Error loading availability types:', error);
    }
  }

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleWindowResize = () => {
      setMapHeight((current) => {
        const desired = Math.max(window.innerHeight * 0.6, 400);
        const maxAllowed = Math.max(window.innerHeight - 120, desired);
        return Math.min(Math.max(current, 350), maxAllowed);
      });
    };

    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    resizeStartHeightRef.current = mapHeight;
  }, [mapHeight]);

  useEffect(() => {
    if (!isResizing) return;

    const computeBounds = () => {
      if (typeof window === 'undefined') {
        return { min: 350, max: 900 };
      }
      return {
        min: 350,
        max: Math.max(window.innerHeight - 120, 450),
      };
    };

    const handlePointerMove = (clientY: number) => {
      const { min, max } = computeBounds();
      const delta = clientY - resizeStartYRef.current;
      const nextHeight = Math.min(Math.max(resizeStartHeightRef.current + delta, min), max);
      setMapHeight(nextHeight);
    };

    const handleMouseMove = (event: MouseEvent) => {
      event.preventDefault();
      handlePointerMove(event.clientY);
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      handlePointerMove(event.touches[0].clientY);
    };

    const stopResizing = () => {
      setIsResizing(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopResizing);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', stopResizing);
    window.addEventListener('touchcancel', stopResizing);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopResizing);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', stopResizing);
      window.removeEventListener('touchcancel', stopResizing);
    };
  }, [isResizing]);

  function startResize(clientY: number) {
    resizeStartYRef.current = clientY;
    resizeStartHeightRef.current = mapHeight;
    setIsResizing(true);
  }

  function handleResizeMouseDown(event: ReactMouseEvent<HTMLDivElement>) {
    event.preventDefault();
    startResize(event.clientY);
  }

  function handleResizeTouchStart(event: ReactTouchEvent<HTMLDivElement>) {
    if (event.touches.length !== 1) return;
    startResize(event.touches[0].clientY);
  }

  useEffect(() => {
    loadAirports();
    loadMachines(); // Load all machines on mount
    loadPlanograms();
  }, []);

  // Load tiers when venueName changes in new machine form
  useEffect(() => {
    if (newMachine.venueName && newMachine.tenderType === 'long-term') {
      loadTiers(newMachine.venueName);
    } else {
      setAvailableTiers([]);
    }
  }, [newMachine.venueName, newMachine.tenderType]);

  // Load tiers when venueName changes in edit form
  useEffect(() => {
    if (editFormData.venueName && editFormData.tenderType === 'long-term') {
      loadTiers(editFormData.venueName);
    } else {
      setAvailableTiers([]);
    }
  }, [editFormData.venueName, editFormData.tenderType]);

  // Load tiers for bulk edit based on selected machines
  useEffect(() => {
    if (showBulkEditDialog && selectedMachines.length > 0) {
      // Get unique venue names from selected machines
      const venueNames = Array.from(new Set(
        selectedMachines
          .map(m => m.venueName)
          .filter(Boolean) as string[]
      ));
      
      // If all machines have the same venue name, load tiers for that venue
      if (venueNames.length === 1) {
        loadTiers(venueNames[0]);
      } else {
        // Multiple venue names - clear tiers (can't assign to different venues)
        setAvailableTiers([]);
      }
    } else if (!showBulkEditDialog) {
      setAvailableTiers([]);
    }
  }, [showBulkEditDialog, selectedMachines]);

  async function loadPlanograms() {
    try {
      setLoadingPlanograms(true);
      const data = await getPlanograms();
      setAvailablePlanograms(data);
    } catch (error) {
      console.error('Failed to load planograms:', error);
    } finally {
      setLoadingPlanograms(false);
    }
  }

  async function loadTiers(venueName?: string | null) {
    if (!venueName) {
      setAvailableTiers([]);
      return;
    }
    try {
      setLoadingTiers(true);
      const data = await getMachineTiers(venueName);
      setAvailableTiers(data);
    } catch (error) {
      console.error('Failed to load tiers:', error);
      setAvailableTiers([]);
    } finally {
      setLoadingTiers(false);
    }
  }

  useEffect(() => {
    if (selectedAirport) {
      // Keep airport-based map centering for backward compatibility
      const override = airportViewOverrides[selectedAirport.code];
      const center: [number, number] = override?.center ?? [selectedAirport.lat, selectedAirport.lng];
      const baseZoom = override?.zoom ?? selectedAirport.zoom;
      const adjustedZoom = Math.max(baseZoom - DEFAULT_ZOOM_OFFSET, 0);
      setMapCenter(center);
      setMapZoom(adjustedZoom);
    }
  }, [selectedAirport]);

  async function loadAirports() {
    try {
      const data = await getAirports();
      setAirports(data);
      if (data.length > 0) {
        setSelectedAirport(data[0]);
      }
    } catch (error) {
      console.error('Failed to load airports:', error);
    }
  }

  async function loadMachines(code?: string) {
    try {
      // Load all machines (not filtered by airport)
      const data = await getMachines();
      setMachines(data);
      
      // Expand all venue types by default
      const venueTypes = new Set(data.map(m => m.venueType || 'Uncategorized'));
      setExpandedVenueTypes(venueTypes);
    } catch (error) {
      console.error('Failed to load machines:', error);
    }
  }

  function toggleVenueType(venueType: string) {
    setExpandedVenueTypes(prev => {
      const next = new Set(prev);
      if (next.has(venueType)) {
        next.delete(venueType);
      } else {
        next.add(venueType);
      }
      return next;
    });
  }

  function toggleSubcategory(venueType: string, venueName: string) {
    const key = `${venueType}:${venueName}`;
    setExpandedSubcategories(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  // Group machines by venue type and venue name
  const groupedMachines = useMemo(() => {
    const groups = new globalThis.Map<string, globalThis.Map<string, Machine[]>>();
    
    machines.forEach((machine) => {
      const venueType = machine.venueType || 'Uncategorized';
      const venueName = machine.venueName || 'Other';
      
      if (!groups.has(venueType)) {
        groups.set(venueType, new globalThis.Map());
      }
      const venueMap = groups.get(venueType)!;
      
      if (!venueMap.has(venueName)) {
        venueMap.set(venueName, []);
      }
      venueMap.get(venueName)!.push(machine);
    });
    
    return groups;
  }, [machines]);

  function handleMapClick(lat: number, lng: number) {
    if (moveMode && selectedMachine) {
      // Handle moving pin
      handleMovePin(lat, lng);
      return;
    }
    
    if (!dropPinMode) return;
    
    setNewMachine({
      ...newMachine,
      lat,
      lng,
    });
    setShowDialog(true);
    setDropPinMode(false);
    setSearchQuery('');
    setSearchResults([]);
  }

  async function searchLocation(query: string) {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`,
        {
          headers: {
            'User-Agent': 'BrandConfigurator/1.0'
          }
        }
      );
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Failed to search location:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }

  function handleSearchResultClick(result: any) {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    
    // Center map on the selected location
    setMapCenter([lat, lng]);
    setMapZoom(16);
    
    // Clear search but keep drop pin mode active
    setSearchQuery('');
    setSearchResults([]);
    
    // User will manually click on the map to drop the pin
  }

  function handleMachineClick(machine: Machine) {
    setSelectedMachine(machine);
  }

  function handleEditClick() {
    if (!selectedMachine) return;
    
    try {
      const areaMedia = selectedMachine.MachineMedia?.find(m => m.kind === 'area');
      const renderMedia = selectedMachine.MachineMedia?.find(m => m.kind === 'render');
      
      setEditFormData({
        name: selectedMachine.name || '',
        description: selectedMachine.description || '',
        cost: selectedMachine.costCents ? (selectedMachine.costCents / 100).toString() : '0',
        label: selectedMachine.label || '',
        pinColor: selectedMachine.pinColor || '#3B82F6',
        isActive: selectedMachine.isActive ?? true,
        status: (selectedMachine.status as 'Live/Available' | 'Reserved' | 'Coming Soon') || 'Live/Available',
        availabilityType: selectedMachine.availabilityType || '',
        availableSkus: selectedMachine.availableSkus !== null && selectedMachine.availableSkus !== undefined ? selectedMachine.availableSkus.toString() : '',
        areaImageUrl: areaMedia?.url || '',
        renderImageUrl: renderMedia?.url || '',
        venueType: selectedMachine.venueType || '',
        venueName: selectedMachine.venueName || '',
        machineType: selectedMachine.machineType || '',
        resolution: selectedMachine.resolution || '',
        location: selectedMachine.location || '',
        capacity: selectedMachine.capacity || '',
        format: selectedMachine.format || '',
        screenSize: selectedMachine.screenSize || '',
        playbackFormat: selectedMachine.playbackFormat || '',
        duration: selectedMachine.duration || '',
        orientation: selectedMachine.orientation || '',
        aspectRatio: selectedMachine.aspectRatio || '',
        addressLine1: selectedMachine.addressLine1 || '',
        addressLine2: selectedMachine.addressLine2 || '',
        postCode: selectedMachine.postCode || '',
        county: selectedMachine.county || '',
        city: selectedMachine.city || '',
        requiresTenderProcess: selectedMachine.requiresTenderProcess || false,
        planogramConfig: typeof selectedMachine.planogramConfig === 'string' ? selectedMachine.planogramConfig : '',
        planogramId: (selectedMachine as any).planogramId || '',
        tenderTimeframe: selectedMachine.tenderTimeframe || '',
        tenderLaunchDate: selectedMachine.tenderLaunchDate || '',
        tenderType: (selectedMachine as any).tenderType || '',
        minimumMachines: (selectedMachine as any).minimumMachines || '',
        machineTierId: (selectedMachine as any).machineTierId || '',
        footfall: (selectedMachine as any).footfall != null ? String((selectedMachine as any).footfall) : '',
        priceFromPer2Weeks: (selectedMachine as any).priceFromPer2WeeksCents != null ? ((selectedMachine as any).priceFromPer2WeeksCents / 100).toString() : '',
      });
      setRemoveAreaImage(false);
      setRemoveRenderImage(false);
      setShowEditDialog(true);
    } catch (error) {
      console.error('Error opening edit dialog:', error);
      alert('Failed to open edit dialog. Please try again.');
    }
  }

  function handleMoveClick() {
    setMoveMode(true);
    setDropPinMode(false);
  }

  async function handleMovePin(lat: number, lng: number) {
    if (!selectedMachine) return;
    
    try {
      await updateMachine(selectedMachine.id, { lat, lng });
      await loadMachines(); // Load all machines
      setMoveMode(false);
      setSelectedMachine(null);
    } catch (error) {
      console.error('Failed to move machine:', error);
      alert('Failed to move machine');
    }
  }

  async function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedMachine) return;

    // Validate required fields
    if (!editFormData.venueType || editFormData.venueType.trim() === '') {
      alert('Venue Type is required');
      return;
    }

    // Validate tender process fields
    if (editFormData.requiresTenderProcess) {
      if (!editFormData.tenderType || editFormData.tenderType.trim() === '') {
        alert('Tender Type is required when "Requires Tender Process" is checked');
        return;
      }
      if (!editFormData.venueName || editFormData.venueName.trim() === '') {
        alert('Venue Name is required when "Requires Tender Process" is checked (needed for grouping)');
        return;
      }
    }

    try {
      // Upload new images if selected
      let areaImageUrl = editFormData.areaImageUrl;
      let renderImageUrl = editFormData.renderImageUrl;

      if (editAreaImageFile) {
        setUploadingEditAreaImage(true);
        areaImageUrl = await uploadImage(editAreaImageFile);
        setUploadingEditAreaImage(false);
      }

      if (editRenderImageFile) {
        setUploadingEditRenderImage(true);
        renderImageUrl = await uploadImage(editRenderImageFile);
        setUploadingEditRenderImage(false);
      }

      const costCents = editFormData.cost ? centsFromPounds(parseFloat(editFormData.cost)) : undefined;
      const priceFromPer2WeeksCents = editFormData.priceFromPer2Weeks ? centsFromPounds(parseFloat(editFormData.priceFromPer2Weeks)) : undefined;
      const footfall = editFormData.footfall !== '' && editFormData.footfall != null ? parseInt(String(editFormData.footfall), 10) : undefined;
      
      await updateMachine(selectedMachine.id, {
        name: editFormData.name,
        description: editFormData.description || undefined,
        costCents,
        label: editFormData.label || undefined,
        pinColor: editFormData.pinColor || undefined,
        isActive: editFormData.isActive,
        status: editFormData.status || undefined,
        availabilityType: editFormData.availabilityType || undefined,
        availableSkus: editFormData.availableSkus ? parseInt(editFormData.availableSkus.toString(), 10) : null,
        venueType: editFormData.venueType, // Required field
        venueName: editFormData.venueName || undefined, // New field
        machineType: editFormData.machineType || undefined,
        resolution: editFormData.resolution || undefined,
        location: editFormData.location || undefined,
        capacity: editFormData.capacity || undefined,
        format: editFormData.format || undefined,
        screenSize: editFormData.screenSize || undefined,
        playbackFormat: editFormData.playbackFormat || undefined,
        duration: editFormData.duration || undefined,
        orientation: editFormData.orientation || undefined,
        aspectRatio: editFormData.aspectRatio || undefined,
        addressLine1: editFormData.addressLine1 || undefined,
        addressLine2: editFormData.addressLine2 || undefined,
        postCode: editFormData.postCode || undefined,
        county: editFormData.county || undefined,
        city: editFormData.city || undefined,
        requiresTenderProcess: editFormData.requiresTenderProcess,
        planogramConfig: editFormData.planogramId ? undefined : (editFormData.planogramConfig || undefined),
        planogramId: editFormData.planogramId || undefined,
        tenderTimeframe: editFormData.tenderTimeframe || undefined,
        tenderLaunchDate: editFormData.tenderLaunchDate || undefined,
        tenderType: editFormData.tenderType || undefined,
        minimumMachines: editFormData.minimumMachines ? parseInt(editFormData.minimumMachines.toString(), 10) : undefined,
        machineTierId: editFormData.machineTierId || undefined,
        footfall: footfall,
        priceFromPer2WeeksCents: priceFromPer2WeeksCents,
      });

      // Update or create media
      const areaMedia = selectedMachine.MachineMedia?.find(m => m.kind === 'area');
      const renderMedia = selectedMachine.MachineMedia?.find(m => m.kind === 'render');

      // Handle area image
      if (removeAreaImage && areaMedia) {
        // User requested to remove the image
        await deleteMachineMedia(selectedMachine.id, areaMedia.id);
      } else if (areaImageUrl && !removeAreaImage) {
        // Only process if not removing and URL exists
        // If media exists and URL changed, delete old and create new
        if (areaMedia && areaMedia.url !== areaImageUrl) {
          await deleteMachineMedia(selectedMachine.id, areaMedia.id);
          await createMachineMedia(selectedMachine.id, {
            kind: 'area',
            url: areaImageUrl,
            sortOrder: 1,
          });
        } else if (!areaMedia) {
          // Create new media if it doesn't exist
          await createMachineMedia(selectedMachine.id, {
            kind: 'area',
            url: areaImageUrl,
            sortOrder: 1,
          });
        }
      }

      // Handle render image
      if (removeRenderImage && renderMedia) {
        // User requested to remove the image
        await deleteMachineMedia(selectedMachine.id, renderMedia.id);
      } else if (renderImageUrl && !removeRenderImage) {
        // Only process if not removing and URL exists
        // If media exists and URL changed, delete old and create new
        if (renderMedia && renderMedia.url !== renderImageUrl) {
          await deleteMachineMedia(selectedMachine.id, renderMedia.id);
          await createMachineMedia(selectedMachine.id, {
            kind: 'render',
            url: renderImageUrl,
            sortOrder: 2,
          });
        } else if (!renderMedia) {
          // Create new media if it doesn't exist
          await createMachineMedia(selectedMachine.id, {
            kind: 'render',
            url: renderImageUrl,
            sortOrder: 2,
          });
        }
      }

      setShowEditDialog(false);
      setSelectedMachine(null);
      setEditAreaImageFile(null);
      setEditRenderImageFile(null);
      setRemoveAreaImage(false);
      setRemoveRenderImage(false);
      await loadMachines();
    } catch (error) {
      console.error('Failed to update machine:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update machine';
      alert(`Failed to update machine: ${errorMessage}`);
      setUploadingEditAreaImage(false);
      setUploadingEditRenderImage(false);
    }
  }

  async function handleDeleteConfirm() {
    if (!selectedMachine) return;

    try {
      await deleteMachine(selectedMachine.id);
      setShowDeleteDialog(false);
      setSelectedMachine(null);
      await loadMachines();
    } catch (error) {
      console.error('Failed to delete machine:', error);
      alert('Failed to delete machine');
    }
  }

  // Bulk operations
  function handleToggleMachineSelection(machine: Machine) {
    setSelectedMachines(prev => {
      const isAlreadySelected = prev.some(m => m.id === machine.id);
      if (isAlreadySelected) {
        return prev.filter(m => m.id !== machine.id);
      } else {
        return [...prev, machine];
      }
    });
  }

  function handleSelectAll() {
    if (selectedMachines.length === machines.length) {
      setSelectedMachines([]);
    } else {
      setSelectedMachines([...machines]);
    }
  }

  function handleClearSelection() {
    setSelectedMachines([]);
  }

  async function handleBulkDelete() {
    if (selectedMachines.length === 0) return;

    try {
      await Promise.all(selectedMachines.map(machine => deleteMachine(machine.id)));
      setShowBulkDeleteDialog(false);
      setSelectedMachines([]);
      await loadMachines();
    } catch (error) {
      console.error('Failed to delete machines:', error);
      alert('Failed to delete some machines');
    }
  }

  async function uploadImage(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch('/api/upload/image', {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to upload image');
    }

    const data = await response.json();
    return data.url;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Validate required fields
    if (!newMachine.venueType || newMachine.venueType.trim() === '') {
      alert('Venue Type is required');
      return;
    }

    // Validate tender process fields
    if (newMachine.requiresTenderProcess) {
      if (!newMachine.tenderType || newMachine.tenderType.trim() === '') {
        alert('Tender Type is required when "Requires Tender Process" is checked');
        return;
      }
      if (!newMachine.venueName || newMachine.venueName.trim() === '') {
        alert('Venue Name is required when "Requires Tender Process" is checked (needed for grouping)');
        return;
      }
    }

    try {
      // Upload images if selected
      let areaImageUrl = newMachine.areaImageUrl;
      let renderImageUrl = newMachine.renderImageUrl;

      if (areaImageFile) {
        setUploadingAreaImage(true);
        areaImageUrl = await uploadImage(areaImageFile);
        setUploadingAreaImage(false);
      }

      if (renderImageFile) {
        setUploadingRenderImage(true);
        renderImageUrl = await uploadImage(renderImageFile);
        setUploadingRenderImage(false);
      }

      const costCents = newMachine.cost ? centsFromPounds(parseFloat(newMachine.cost)) : undefined;
      
      const machine = await createMachine({
        airportId: selectedAirport?.id, // Optional for backward compatibility
        name: newMachine.name,
        lat: newMachine.lat,
        lng: newMachine.lng,
        description: newMachine.description || undefined,
        costCents,
        label: newMachine.label || undefined,
        pinColor: newMachine.pinColor || undefined,
        isActive: newMachine.isActive,
        status: newMachine.status,
        availabilityType: newMachine.availabilityType || undefined,
        availableSkus: newMachine.availableSkus ? parseInt(newMachine.availableSkus.toString(), 10) : null,
        venueType: newMachine.venueType, // Required field
        venueName: newMachine.venueName || undefined, // New field
        machineType: newMachine.machineType || undefined,
        resolution: newMachine.resolution || undefined,
        location: newMachine.location || undefined,
        capacity: newMachine.capacity || undefined,
        format: newMachine.format || undefined,
        screenSize: newMachine.screenSize || undefined,
        playbackFormat: newMachine.playbackFormat || undefined,
        duration: newMachine.duration || undefined,
        orientation: newMachine.orientation || undefined,
        aspectRatio: newMachine.aspectRatio || undefined,
        addressLine1: newMachine.addressLine1 || undefined,
        addressLine2: newMachine.addressLine2 || undefined,
        postCode: newMachine.postCode || undefined,
        county: newMachine.county || undefined,
        city: newMachine.city || undefined,
        requiresTenderProcess: newMachine.requiresTenderProcess,
        planogramConfig: newMachine.planogramId ? undefined : (newMachine.planogramConfig || undefined),
        planogramId: newMachine.planogramId || undefined,
        tenderTimeframe: newMachine.tenderTimeframe || undefined,
        tenderLaunchDate: newMachine.tenderLaunchDate || undefined,
        tenderType: newMachine.tenderType || undefined,
        minimumMachines: newMachine.minimumMachines ? parseInt(newMachine.minimumMachines.toString(), 10) : undefined,
        machineTierId: (newMachine as any).machineTierId || undefined,
      });

      // Add media if URLs provided or images uploaded
      if (areaImageUrl) {
        await createMachineMedia(machine.id, {
          kind: 'area',
          url: areaImageUrl,
          sortOrder: 0,
        });
      }

      if (renderImageUrl) {
        await createMachineMedia(machine.id, {
          kind: 'render',
          url: renderImageUrl,
          sortOrder: 0,
        });
      }

      await loadMachines();
      setShowDialog(false);
      setNewMachine({
        lat: 0,
        lng: 0,
        name: '',
        description: '',
        cost: '',
        label: '',
        pinColor: '#3B82F6',
        isActive: true,
        status: 'Live/Available' as 'Live/Available' | 'Reserved' | 'Coming Soon',
        availabilityType: '' as string,
        availableSkus: '' as string | number,
        areaImageUrl: '',
        renderImageUrl: '',
        venueType: '',
        venueName: '',
        machineType: '',
        resolution: '',
        location: '',
        capacity: '',
        format: '',
        screenSize: '',
        playbackFormat: '',
        duration: '',
        orientation: '',
        aspectRatio: '',
        addressLine1: '',
        addressLine2: '',
        postCode: '',
        county: '',
        city: '',
        requiresTenderProcess: false,
        planogramConfig: '',
        planogramId: '' as string,
        tenderTimeframe: '',
        tenderLaunchDate: '',
        tenderType: '' as 'long-term' | 'short-term' | '',
        minimumMachines: '' as string | number,
        machineTierId: '' as string,
      });
      setAreaImageFile(null);
      setRenderImageFile(null);
    } catch (error) {
      console.error('Failed to create machine:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create machine';
      alert(`Failed to create machine: ${errorMessage}`);
      setUploadingAreaImage(false);
      setUploadingRenderImage(false);
    }
  }


  const mapMarkers: MapMarker[] = machines.map((machine) => ({
    lat: machine.lat,
    lng: machine.lng,
    title: machine.label ? `${machine.name} (${machine.label})` : machine.name,
    color: machine.pinColor || undefined,
    onClick: () => handleMachineClick(machine),
  }));

  const slug = newMachine.name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();

  return (
    <div className="h-full flex flex-col">
      <div className="px-2 md:px-4 pb-2 md:pb-4">
        <div className="flex flex-col gap-2 pt-2">
          <div className="bg-white border rounded-lg px-4 py-3 relative z-50">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">Machine Editor</h1>
                <p className="text-sm text-muted-foreground">Manage machine locations</p>
              </div>
              <div className="flex items-center gap-4">
                <Button
                  onClick={() => setDropPinMode(!dropPinMode)}
                  variant={dropPinMode ? 'default' : 'outline'}
                >
                  {dropPinMode ? 'Cancel' : 'Add Machine'}
                </Button>
                <Button
                  onClick={() => {
                    setNewMachine({
                      ...newMachine,
                      lat: 52.9548, // Default UK center
                      lng: -1.1581,
                    });
                    setShowDialog(true);
                    setDropPinMode(false);
                  }}
                  variant="outline"
                >
                  Enter Coordinates
                </Button>
              </div>
            </div>
          </div>

          <div
            className="border rounded-lg overflow-hidden bg-white shadow-sm relative select-none group"
            style={{ height: `${mapHeight}px` }}
          >
            {dropPinMode && (
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[999] w-full max-w-md px-4">
                <div className="bg-white rounded-lg shadow-xl border border-slate-200">
                  <div className="p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Search className="h-4 w-4 text-slate-400" />
                      <Input
                        type="text"
                        placeholder="Search for a place (e.g., Manchester Airport, London Eye)..."
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          if (e.target.value.length > 2) {
                            searchLocation(e.target.value);
                          } else {
                            setSearchResults([]);
                          }
                        }}
                        className="flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                      />
                      {searching && (
                        <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                      )}
                    </div>
                    {searchResults.length > 0 && (
                      <div className="border-t border-slate-100 mt-2 pt-2 max-h-60 overflow-y-auto">
                        {searchResults.map((result, index) => (
                          <button
                            key={index}
                            onClick={() => handleSearchResultClick(result)}
                            className="w-full text-left p-2 hover:bg-slate-50 rounded flex items-start gap-2 transition-colors"
                          >
                            <MapPin className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-slate-900 truncate">
                                {result.display_name}
                              </div>
                              <div className="text-xs text-slate-500">
                                {result.type} · {result.lat.substring(0, 8)}, {result.lon.substring(0, 8)}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="bg-blue-50 px-3 py-2 text-xs text-blue-700 border-t border-blue-100 rounded-b-lg">
                    💡 Search for a place above or click directly on the map to drop a pin
                  </div>
                </div>
              </div>
            )}
            {moveMode && selectedMachine && (
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[999] bg-purple-600 text-white px-4 py-2 rounded-lg shadow-lg">
                Click on the map to move "{selectedMachine.name}" to new location
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-4 text-white hover:bg-purple-700"
                  onClick={() => {
                    setMoveMode(false);
                    setSelectedMachine(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
            {selectedMachine && !moveMode && !dropPinMode && (
              <div className="absolute top-4 right-4 z-[999] bg-white border rounded-lg shadow-lg p-4 max-w-sm">
                <div className="flex items-start gap-3 mb-3">
                  {selectedMachine.pinColor && (
                    <div
                      className="w-8 h-8 rounded-full border-2 border-gray-300"
                      style={{ backgroundColor: selectedMachine.pinColor }}
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{selectedMachine.name}</h3>
                      {selectedMachine.label && (
                        <span className="text-xs px-2 py-0.5 bg-muted rounded-full">
                          {selectedMachine.label}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {formatCurrency(selectedMachine.costCents)}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedMachine(null)}
                  >
                    ×
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={handleEditClick}
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={handleMoveClick}
                  >
                    <Move className="w-4 h-4 mr-1" />
                    Move
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 text-red-600 hover:text-red-700"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            )}
            <Map
              center={mapCenter}
              zoom={mapZoom}
              markers={mapMarkers}
              onMapClick={handleMapClick}
              dropPinMode={dropPinMode || moveMode}
              mapStyle={mapStyle}
            />
            {/* Map Style Picker */}
            <div className="absolute bottom-4 left-4 z-[1000]">
              <Select value={mapStyle} onValueChange={(value: MapStyle) => setMapStyle(value)}>
                <SelectTrigger className="w-[75px] h-[75px] rounded-xl border-0 bg-black/60 backdrop-blur-sm text-white shadow-lg hover:bg-black/70 transition-all p-2 flex flex-col items-center justify-center gap-1">
                  <Layers className="w-6 h-6" />
                  <span className="text-xs font-medium">Layers</span>
                </SelectTrigger>
                <SelectContent className="z-[2000] w-[200px] rounded-xl border-0 shadow-xl bg-white p-1">
                  {Object.entries(MAP_STYLES).map(([key, config]) => (
                    <SelectItem 
                      key={key} 
                      value={key}
                      className="rounded-lg pl-8 pr-3 py-2 cursor-pointer hover:bg-slate-100 focus:bg-slate-100 relative"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium text-slate-900">{config.name}</span>
                        <span className="text-xs text-slate-500">{config.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div
              className={`absolute bottom-0 left-0 right-0 h-4 cursor-row-resize transition-colors pointer-events-auto z-20 ${
                isResizing ? 'bg-muted/60' : 'group-hover:bg-muted/40'
              }`}
              onMouseDown={handleResizeMouseDown}
              onTouchStart={handleResizeTouchStart}
              role="presentation"
            />
          </div>

          {/* Machine List */}
          {machines.length > 0 && (
            <div className="bg-white border rounded-lg p-4 mt-2">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    All Machines
                  </h2>
                  <p className="text-sm text-slate-500">
                    {machines.length} machine{machines.length !== 1 ? 's' : ''} • Click on a machine to view details
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={selectedMachines.length === machines.length && machines.length > 0}
                    onCheckedChange={handleSelectAll}
                    className="h-5 w-5"
                  />
                  <span className="text-sm text-slate-600">Select All</span>
                </div>
              </div>
              
              {selectedMachines.length > 0 && (
                <div className="mb-3 flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <span className="text-sm font-semibold text-blue-900">
                    {selectedMachines.length} machine{selectedMachines.length !== 1 ? 's' : ''} selected
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearSelection}
                    className="h-7 px-2 text-xs"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Clear
                  </Button>
                  <div className="ml-auto flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Initialize bulk edit form with "keep existing" defaults
                        setEditFormData({
                          name: '',
                          description: '',
                          cost: '',
                          label: '',
                          pinColor: '#3B82F6',
                          isActive: true,
                          status: '' as 'Live/Available' | 'Reserved' | 'Coming Soon' | '',
                          availabilityType: '',
                          availableSkus: '',
                          areaImageUrl: '',
                          renderImageUrl: '',
                          venueType: '',
                          venueName: '',
                          machineType: '',
                          resolution: '',
                          location: '',
                          capacity: '',
                          format: '',
                          screenSize: '',
                          playbackFormat: '',
                          duration: '',
                          orientation: '',
                          aspectRatio: '',
                          addressLine1: '',
                          addressLine2: '',
                          postCode: '',
                          county: '',
                          city: '',
                          requiresTenderProcess: false,
                          planogramConfig: '',
                          planogramId: '' as string,
                          tenderTimeframe: '',
                          tenderLaunchDate: '',
                          tenderType: '' as 'long-term' | 'short-term' | '',
                          minimumMachines: '' as string | number,
                          machineTierId: '' as string,
                          footfall: '' as string | number,
                          priceFromPer2Weeks: '',
                        });
                        setShowBulkEditDialog(true);
                      }}
                      className="h-8"
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Bulk Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowBulkDeleteDialog(true)}
                      className="h-8"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete ({selectedMachines.length})
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="space-y-4">
                {Array.from(groupedMachines.entries()).map(([venueType, subcategoriesMap]) => {
                  if (!subcategoriesMap || subcategoriesMap.size === 0) return null;

                  // Calculate total machines across all subcategories
                  let totalMachines = 0;
                  subcategoriesMap.forEach((machines) => {
                    totalMachines += machines.length;
                  });

                  const isVenueTypeExpanded = expandedVenueTypes.has(venueType);

                  // Get all machines in this venue type
                  const allMachinesInVenueType: Machine[] = [];
                  subcategoriesMap.forEach((machines) => {
                    allMachinesInVenueType.push(...machines);
                  });

                  // Count selected machines in this venue type
                  const selectedInVenueType = allMachinesInVenueType.filter(m => 
                    selectedMachines.some(sm => sm.id === m.id)
                  ).length;

                  return (
                    <div key={venueType} className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                      {/* Venue Type Header */}
                      <div className="flex items-center gap-2 p-4 hover:bg-slate-50">
                        <button
                          onClick={() => toggleVenueType(venueType)}
                          className="flex-1 flex items-center gap-3"
                        >
                          <ChevronDown 
                            className={`h-5 w-5 text-slate-400 transition-transform ${isVenueTypeExpanded ? 'transform rotate-0' : 'transform -rotate-90'}`}
                          />
                          <div className="text-left">
                            <h3 className="text-base font-semibold text-slate-900">{venueType}</h3>
                            <p className="text-xs text-slate-500">
                              {totalMachines} {totalMachines === 1 ? 'Machine' : 'Machines'}
                              {selectedInVenueType > 0 && ` • ${selectedInVenueType} selected`}
                            </p>
                          </div>
                        </button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Check if all machines in this venue type are selected
                            const allSelected = allMachinesInVenueType.every(m => 
                              selectedMachines.some(sm => sm.id === m.id)
                            );
                            
                            if (allSelected) {
                              // Deselect all
                              setSelectedMachines(prev => 
                                prev.filter(sm => !allMachinesInVenueType.some(m => m.id === sm.id))
                              );
                            } else {
                              // Select all
                              const newSelections = allMachinesInVenueType.filter(m => 
                                !selectedMachines.some(sm => sm.id === m.id)
                              );
                              setSelectedMachines(prev => [...prev, ...newSelections]);
                            }
                          }}
                          className="text-xs"
                        >
                          {selectedInVenueType === totalMachines ? 'Deselect All' : 'Select All'}
                        </Button>
                      </div>

                      {/* Subcategories */}
                      {isVenueTypeExpanded && (
                        <div className="border-t border-slate-100">
                          {Array.from(subcategoriesMap.entries()).map(([venueName, machines]) => {
                            const subcategoryKey = `${venueType}:${venueName}`;
                            const isSubcategoryExpanded = expandedSubcategories.has(subcategoryKey);

                            return (
                              <div key={subcategoryKey} className="border-b border-slate-100 last:border-b-0">
                                {/* Subcategory Header */}
                                <button
                                  onClick={() => toggleSubcategory(venueType, venueName)}
                                  className="w-full p-3 pl-8 flex items-center justify-between hover:bg-slate-50 transition-colors bg-slate-50/50"
                                >
                                  <div className="flex items-center gap-2">
                                    <ChevronDown 
                                      className={`h-4 w-4 text-slate-400 transition-transform ${isSubcategoryExpanded ? 'transform rotate-0' : 'transform -rotate-90'}`}
                                    />
                                    <div className="text-left">
                                      <h4 className="text-sm font-semibold text-slate-700">{venueName}</h4>
                                      <p className="text-xs text-slate-500">
                                        {machines.length} {machines.length === 1 ? 'Machine' : 'Machines'}
                                      </p>
                                    </div>
                                  </div>
                                </button>

                                {/* Machine List */}
                                {isSubcategoryExpanded && (
                                  <div className="divide-y divide-slate-100 bg-white">
                                    {machines.map((machine) => {
                                      const isSelected = selectedMachine?.id === machine.id;
                                      const isChecked = selectedMachines.some(m => m.id === machine.id);
                                      const venueType = machine.venueType || '—';
                                      const location = machine.location || '—';
                                      const machineType = machine.machineType || '—';
                                      const resolution = machine.resolution || '—';
                                      
                                      return (
                                        <div
                                          key={machine.id}
                                          className={`flex flex-col gap-4 p-4 transition-colors md:flex-row md:items-center md:justify-between ${
                                            isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'
                                          }`}
                                          onClick={() => handleMachineClick(machine)}
                                        >
                                          <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center">
                                            <div className="flex items-center gap-3">
                                              <Checkbox
                                                checked={isChecked}
                                                onCheckedChange={() => handleToggleMachineSelection(machine)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="h-5 w-5"
                                              />
                                              <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100 text-xs font-semibold text-blue-700 text-center p-1">
                                                {machine.venueName?.substring(0, 3).toUpperCase() || machine.venueType?.substring(0, 3).toUpperCase() || 'N/A'}
                                              </div>
                                            </div>
                                            <div className="flex-1 space-y-2">
                                              <div className="flex flex-wrap items-center justify-between gap-2">
                                                <div>
                                                  <h3 className="text-base font-semibold text-slate-900">
                                                    {machine.name}
                                                  </h3>
                                                  {machine.description && (
                                                    <p className="text-sm text-slate-500 line-clamp-1">
                                                      {machine.description}
                                                    </p>
                                                  )}
                                                </div>
                                                <span
                                                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                                                    machine.isActive
                                                      ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                                                      : 'bg-slate-100 text-slate-700 border border-slate-200'
                                                  }`}
                                                >
                                                  {machine.isActive ? 'Available' : 'Reserved'}
                                                </span>
                                              </div>
                                              <div className="grid gap-4 text-sm text-slate-600 sm:grid-cols-4">
                                                <div>
                                                  <p className="text-xs uppercase text-slate-400">Venue Type</p>
                                                  <p className="font-medium">{venueType}</p>
                                                </div>
                                                <div>
                                                  <p className="text-xs uppercase text-slate-400">Location</p>
                                                  <p className="font-medium">{location}</p>
                                                </div>
                                                <div>
                                                  <p className="text-xs uppercase text-slate-400">Machine Type</p>
                                                  <p className="font-medium">{machineType}</p>
                                                </div>
                                                <div>
                                                  <p className="text-xs uppercase text-slate-400">Resolution</p>
                                                  <p className="font-medium">{resolution}</p>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditClick();
                                              }}
                                              disabled={!isSelected}
                                            >
                                              <Pencil className="w-4 h-4" />
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleMoveClick();
                                              }}
                                              disabled={!isSelected}
                                            >
                                              <Move className="w-4 h-4" />
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setShowDeleteDialog(true);
                                              }}
                                              disabled={!isSelected}
                                            >
                                              <Trash2 className="w-4 h-4 text-red-600" />
                                            </Button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Machine Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Machine</DialogTitle>
          </DialogHeader>
          
          {/* Mode Toggle */}
          <div className="flex gap-2 border-b pb-3">
            <Button
              type="button"
              variant={uploadMode === 'manual' ? 'default' : 'outline'}
              onClick={() => setUploadMode('manual')}
              className="flex-1"
            >
              Manual Entry
            </Button>
            <Button
              type="button"
              variant={uploadMode === 'csv' ? 'default' : 'outline'}
              onClick={() => setUploadMode('csv')}
              className="flex-1"
            >
              CSV Upload
            </Button>
          </div>

          {uploadMode === 'csv' ? (
            <BulkCSVUpload
              onComplete={() => {
                loadMachines();
              }}
            />
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={newMachine.name}
                onChange={(e) => setNewMachine({ ...newMachine, name: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="slug">Slug (auto-generated)</Label>
              <Input id="slug" value={slug} disabled className="bg-muted" />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <textarea
                id="description"
                value={newMachine.description}
                onChange={(e) => setNewMachine({ ...newMachine, description: e.target.value })}
                className="w-full min-h-[100px] px-3 py-2 border rounded-md"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="label">Label</Label>
                <CustomComboBox
                  fieldName="label"
                  value={newMachine.label}
                  onChange={(value, colorValue) => {
                    setNewMachine({ 
                      ...newMachine, 
                      label: value,
                      pinColor: colorValue || newMachine.pinColor 
                    });
                  }}
                  placeholder="Select label..."
                />
              </div>

              <div>
                <Label htmlFor="pinColor">Pin Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="pinColor"
                    type="color"
                    value={newMachine.pinColor}
                    onChange={(e) => setNewMachine({ ...newMachine, pinColor: e.target.value })}
                    className="w-20 h-10 p-1"
                  />
                  <Input
                    type="text"
                    value={newMachine.pinColor}
                    onChange={(e) => setNewMachine({ ...newMachine, pinColor: e.target.value })}
                    placeholder="#3B82F6"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="cost">Cost (GBP)</Label>
              <Input
                id="cost"
                type="number"
                step="0.01"
                min="0"
                value={newMachine.cost}
                onChange={(e) => setNewMachine({ ...newMachine, cost: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="venueType">Venue Type *</Label>
                <CustomComboBox
                  fieldName="venueType"
                  value={newMachine.venueType}
                  onChange={(value) => setNewMachine({ ...newMachine, venueType: value })}
                  placeholder="Select venue type..."
                />
              </div>

              <div>
                <Label htmlFor="venueName">
                  Venue Name
                  {newMachine.requiresTenderProcess && <span className="text-red-600 ml-1">*</span>}
                </Label>
                <VenueNameComboBox
                  value={newMachine.venueName}
                  onChange={(value) => setNewMachine({ ...newMachine, venueName: value })}
                  venueType={newMachine.venueType}
                  placeholder="Select or type venue name..."
                />
                {newMachine.requiresTenderProcess && !newMachine.venueName && (
                  <p className="text-xs text-red-600 mt-1">Venue Name is required for tender machines (needed for grouping)</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="machineType">Machine Type</Label>
                <Input
                  id="machineType"
                  value={newMachine.machineType}
                  onChange={(e) => setNewMachine({ ...newMachine, machineType: e.target.value })}
                  placeholder="e.g., Digital Screen"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="resolution">Resolution</Label>
                <Input
                  id="resolution"
                  value={newMachine.resolution}
                  onChange={(e) => setNewMachine({ ...newMachine, resolution: e.target.value })}
                  placeholder="e.g., 1920x1080"
                />
              </div>

              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={newMachine.location}
                  onChange={(e) => setNewMachine({ ...newMachine, location: e.target.value })}
                  placeholder="e.g., Terminal 1, Gate 15"
                />
              </div>
            </div>

            {/* Screen Specifications Section */}
            <div className="pt-4 border-t">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Screen Specifications</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="capacity">Capacity</Label>
                  <CustomComboBox
                    fieldName="capacity"
                    value={newMachine.capacity}
                    onChange={(value) => setNewMachine({ ...newMachine, capacity: value })}
                    placeholder="Select capacity..."
                  />
                </div>

                <div>
                  <Label htmlFor="format">Format</Label>
                  <CustomComboBox
                    fieldName="format"
                    value={newMachine.format}
                    onChange={(value) => setNewMachine({ ...newMachine, format: value })}
                    placeholder="Select format..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <Label htmlFor="screenSize">Screen Size</Label>
                  <CustomComboBox
                    fieldName="screenSize"
                    value={newMachine.screenSize}
                    onChange={(value) => setNewMachine({ ...newMachine, screenSize: value })}
                    placeholder="Select screen size..."
                  />
                </div>

                <div>
                  <Label htmlFor="playbackFormat">Playback Format</Label>
                  <CustomComboBox
                    fieldName="playbackFormat"
                    value={newMachine.playbackFormat}
                    onChange={(value) => setNewMachine({ ...newMachine, playbackFormat: value })}
                    placeholder="Select format..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-4">
                <div>
                  <Label htmlFor="duration">Duration / Loop</Label>
                  <CustomComboBox
                    fieldName="duration"
                    value={newMachine.duration}
                    onChange={(value) => setNewMachine({ ...newMachine, duration: value })}
                    placeholder="Enter duration..."
                  />
                </div>

                <div>
                  <Label htmlFor="orientation">Orientation</Label>
                  <CustomComboBox
                    fieldName="orientation"
                    value={newMachine.orientation}
                    onChange={(value) => setNewMachine({ ...newMachine, orientation: value })}
                    placeholder="Select orientation..."
                  />
                </div>

                <div>
                  <Label htmlFor="aspectRatio">Aspect Ratio</Label>
                  <CustomComboBox
                    fieldName="aspectRatio"
                    value={newMachine.aspectRatio}
                    onChange={(value) => setNewMachine({ ...newMachine, aspectRatio: value })}
                    placeholder="Select ratio..."
                  />
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div className="pt-4 border-t">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Address</h3>
              
              <div>
                <Label htmlFor="addressLine1">Address Line 1</Label>
                <Input
                  id="addressLine1"
                  value={newMachine.addressLine1}
                  onChange={(e) => setNewMachine({ ...newMachine, addressLine1: e.target.value })}
                  placeholder="Street address"
                />
              </div>

              <div className="mt-4">
                <Label htmlFor="addressLine2">Address Line 2</Label>
                <Input
                  id="addressLine2"
                  value={newMachine.addressLine2}
                  onChange={(e) => setNewMachine({ ...newMachine, addressLine2: e.target.value })}
                  placeholder="Apartment, suite, etc. (optional)"
                />
              </div>

              <div className="grid grid-cols-3 gap-4 mt-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={newMachine.city}
                    onChange={(e) => setNewMachine({ ...newMachine, city: e.target.value })}
                    placeholder="City"
                  />
                </div>

                <div>
                  <Label htmlFor="county">County</Label>
                  <Input
                    id="county"
                    value={newMachine.county}
                    onChange={(e) => setNewMachine({ ...newMachine, county: e.target.value })}
                    placeholder="County"
                  />
                </div>

                <div>
                  <Label htmlFor="postCode">Post Code</Label>
                  <Input
                    id="postCode"
                    value={newMachine.postCode}
                    onChange={(e) => setNewMachine({ ...newMachine, postCode: e.target.value })}
                    placeholder="Post code"
                  />
                </div>
              </div>
            </div>

            {/* Images Section */}
            <div className="pt-4 border-t">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Images</h3>
              
              <div>
              <Label htmlFor="areaImage">Area Image</Label>
              <Input
                id="areaImage"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setAreaImageFile(file);
                }}
                disabled={uploadingAreaImage}
              />
              {areaImageFile && (
                <p className="text-sm text-slate-500 mt-1">
                  Selected: {areaImageFile.name}
                </p>
              )}
              {uploadingAreaImage && (
                <p className="text-sm text-blue-600 mt-1">
                  Uploading...
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="renderImage">Render Image</Label>
              <Input
                id="renderImage"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setRenderImageFile(file);
                }}
                disabled={uploadingRenderImage}
              />
              {renderImageFile && (
                <p className="text-sm text-slate-500 mt-1">
                  Selected: {renderImageFile.name}
                </p>
              )}
              {uploadingRenderImage && (
                <p className="text-sm text-blue-600 mt-1">
                  Uploading...
                </p>
              )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                checked={newMachine.isActive}
                onChange={(e) => setNewMachine({ ...newMachine, isActive: e.target.checked })}
              />
              <Label htmlFor="isActive">Active</Label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="latitude">Latitude</Label>
                <Input
                  id="latitude"
                  type="number"
                  step="any"
                  value={newMachine.lat}
                  onChange={(e) => {
                    const lat = parseFloat(e.target.value);
                    if (!isNaN(lat) && lat >= -90 && lat <= 90) {
                      setNewMachine({ ...newMachine, lat });
                    }
                  }}
                  placeholder="e.g., 52.830576"
                  required
                />
              </div>
              <div>
                <Label htmlFor="longitude">Longitude</Label>
                <Input
                  id="longitude"
                  type="number"
                  step="any"
                  value={newMachine.lng}
                  onChange={(e) => {
                    const lng = parseFloat(e.target.value);
                    if (!isNaN(lng) && lng >= -180 && lng <= 180) {
                      setNewMachine({ ...newMachine, lng });
                    }
                  }}
                  placeholder="e.g., -1.329002"
                  required
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              You can either drop a pin on the map or manually enter coordinates above.
            </p>

            <div className="space-y-4 border-t pt-4 mt-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="requiresTenderProcess"
                  checked={newMachine.requiresTenderProcess}
                  onChange={(e) => setNewMachine({ ...newMachine, requiresTenderProcess: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="requiresTenderProcess" className="text-sm font-medium">
                  Requires Tender Process (New Location)
                </Label>
              </div>

              {newMachine.requiresTenderProcess && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="tenderType">Tender Type *</Label>
                      <Select
                        value={newMachine.tenderType || ''}
                        onValueChange={(value) => setNewMachine({ ...newMachine, tenderType: value as 'long-term' | 'short-term' })}
                        required={newMachine.requiresTenderProcess}
                      >
                        <SelectTrigger className={!newMachine.tenderType && newMachine.requiresTenderProcess ? 'border-red-500' : ''}>
                          <SelectValue placeholder="Select tender type..." />
                        </SelectTrigger>
                        <SelectContent className="z-[2000]">
                          <SelectItem value="long-term">Long-term</SelectItem>
                          <SelectItem value="short-term">Short-term</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-1">Required for machines to appear in Long-Term Listings</p>
                      {newMachine.requiresTenderProcess && !newMachine.tenderType && (
                        <p className="text-xs text-red-600 mt-1">Tender Type is required</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="minimumMachines">Minimum Machines</Label>
                      <Input
                        id="minimumMachines"
                        type="number"
                        min="1"
                        value={newMachine.minimumMachines || ''}
                        onChange={(e) => setNewMachine({ ...newMachine, minimumMachines: e.target.value ? parseInt(e.target.value, 10) : '' })}
                        placeholder="e.g., 5"
                      />
                      <p className="text-xs text-slate-500 mt-1">Minimum machines required per venue (optional)</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="tenderTimeframe">Timeframe</Label>
                      <Select
                        value={newMachine.tenderTimeframe || ''}
                        onValueChange={(value) => setNewMachine({ ...newMachine, tenderTimeframe: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select timeframe..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="6 months">6 months</SelectItem>
                          <SelectItem value="12 months">12 months</SelectItem>
                          <SelectItem value="custom">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="tenderLaunchDate">Launch Date</Label>
                      <Input
                        id="tenderLaunchDate"
                        type="date"
                        value={newMachine.tenderLaunchDate || ''}
                        onChange={(e) => setNewMachine({ ...newMachine, tenderLaunchDate: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Planogram</Label>
                    <Select
                      value={newMachine.planogramId || 'none'}
                      onValueChange={(value) => {
                        if (value === 'create-new') {
                          setEditingPlanogram('create');
                          setShowPlanogramModal(true);
                        } else if (value === 'none') {
                          setNewMachine({ ...newMachine, planogramId: '', planogramConfig: '' });
                        } else {
                          setNewMachine({ ...newMachine, planogramId: value, planogramConfig: '' });
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select planogram..." />
                      </SelectTrigger>
                      <SelectContent className="z-[2000]">
                        <SelectItem value="none">None</SelectItem>
                        {availablePlanograms.map((planogram) => (
                          <SelectItem key={planogram.id} value={planogram.id}>
                            {planogram.name} {planogram.machineCount ? `(${planogram.machineCount} machines)` : ''}
                          </SelectItem>
                        ))}
                        <SelectItem value="create-new">+ Create New Planogram</SelectItem>
                      </SelectContent>
                    </Select>
                    {newMachine.planogramId && (
                      <p className="text-xs text-green-600 mt-1">✓ Planogram selected</p>
                    )}
                    {newMachine.planogramConfig && !newMachine.planogramId && (
                      <p className="text-xs text-green-600 mt-1">✓ Planogram configured</p>
                    )}
                  </div>
                  {newMachine.venueName && newMachine.tenderType === 'long-term' && (
                    <div className="space-y-2">
                      <Label>Machine Tier (for Long-Term Tender)</Label>
                      <Select
                        value={(newMachine as any).machineTierId || 'none'}
                        onValueChange={async (value) => {
                          if (value === 'create-new') {
                            setShowCreateTierDialog(true);
                          } else if (value === 'none') {
                            setNewMachine({ ...newMachine, machineTierId: '' as any });
                          } else {
                            setNewMachine({ ...newMachine, machineTierId: value as any });
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select tier..." />
                        </SelectTrigger>
                        <SelectContent className="z-[2000]">
                          <SelectItem value="none">None</SelectItem>
                          {availableTiers.map((tier) => (
                            <SelectItem key={tier.id} value={tier.id}>
                              {tier.name} {tier.machineCount !== undefined ? `(${tier.machineCount} machines)` : ''}
                            </SelectItem>
                          ))}
                          <SelectItem value="create-new">+ Create New Tier</SelectItem>
                        </SelectContent>
                      </Select>
                      {(newMachine as any).machineTierId && (
                        <p className="text-xs text-green-600 mt-1">✓ Tier selected</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Create Machine</Button>
            </div>
          </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Machine Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Machine</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Name *</Label>
              <Input
                id="edit-name"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="edit-description">Description</Label>
              <textarea
                id="edit-description"
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                className="w-full min-h-[100px] px-3 py-2 border rounded-md"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-label">Label</Label>
                <Input
                  id="edit-label"
                  value={editFormData.label}
                  onChange={(e) => setEditFormData({ ...editFormData, label: e.target.value })}
                  placeholder="e.g., Landside, Airside"
                />
              </div>

              <div>
                <Label htmlFor="edit-pinColor">Pin Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="edit-pinColor"
                    type="color"
                    value={editFormData.pinColor}
                    onChange={(e) => setEditFormData({ ...editFormData, pinColor: e.target.value })}
                    className="w-20 h-10 p-1"
                  />
                  <Input
                    type="text"
                    value={editFormData.pinColor}
                    onChange={(e) => setEditFormData({ ...editFormData, pinColor: e.target.value })}
                    placeholder="#3B82F6"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-cost">Cost (GBP)</Label>
                <Input
                  id="edit-cost"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editFormData.cost}
                  onChange={(e) => setEditFormData({ ...editFormData, cost: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-priceFromPer2Weeks">Price from per 2 weeks (GBP)</Label>
                <Input
                  id="edit-priceFromPer2Weeks"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="e.g. 1500"
                  value={editFormData.priceFromPer2Weeks}
                  onChange={(e) => setEditFormData({ ...editFormData, priceFromPer2Weeks: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-footfall">Footfall (per 2 weeks)</Label>
                <Input
                  id="edit-footfall"
                  type="number"
                  min="0"
                  placeholder="e.g. 126000"
                  value={editFormData.footfall}
                  onChange={(e) => setEditFormData({ ...editFormData, footfall: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-status">Status</Label>
                <Select
                  value={editFormData.status}
                  onValueChange={(value: 'Live/Available' | 'Reserved' | 'Coming Soon') =>
                    setEditFormData({ ...editFormData, status: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status..." />
                  </SelectTrigger>
                  <SelectContent className="z-[2000]">
                    <SelectItem value="Live/Available">Live/Available</SelectItem>
                    <SelectItem value="Reserved">Reserved</SelectItem>
                    <SelectItem value="Coming Soon">Coming Soon</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="edit-availabilityType">Availability Type</Label>
                <Select
                  value={editFormData.availabilityType || '__none__'}
                  onValueChange={(value: string) =>
                    setEditFormData({ ...editFormData, availabilityType: value === '__none__' ? '' : value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select availability type..." />
                  </SelectTrigger>
                  <SelectContent className="z-[2000]">
                    <SelectItem value="__none__">None</SelectItem>
                    {availabilityTypeOptions.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-venueType">Venue Type *</Label>
                <CustomComboBox
                  fieldName="venueType"
                  value={editFormData.venueType}
                  onChange={(value) => setEditFormData({ ...editFormData, venueType: value })}
                  placeholder="Select venue type..."
                />
              </div>

              <div>
                <Label htmlFor="edit-venueName">
                  Venue Name
                  {editFormData.requiresTenderProcess && <span className="text-red-600 ml-1">*</span>}
                </Label>
                <VenueNameComboBox
                  value={editFormData.venueName}
                  onChange={(value) => setEditFormData({ ...editFormData, venueName: value })}
                  venueType={editFormData.venueType}
                  placeholder="Select or type venue name..."
                />
                {editFormData.requiresTenderProcess && !editFormData.venueName && (
                  <p className="text-xs text-red-600 mt-1">Venue Name is required for tender machines (needed for grouping)</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-machineType">Machine Type</Label>
                <Input
                  id="edit-machineType"
                  value={editFormData.machineType}
                  onChange={(e) => setEditFormData({ ...editFormData, machineType: e.target.value })}
                  placeholder="e.g., Digital Screen"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-resolution">Resolution</Label>
                <Input
                  id="edit-resolution"
                  value={editFormData.resolution}
                  onChange={(e) => setEditFormData({ ...editFormData, resolution: e.target.value })}
                  placeholder="e.g., 1920x1080"
                />
              </div>

              <div>
                <Label htmlFor="edit-location">Location</Label>
                <Input
                  id="edit-location"
                  value={editFormData.location}
                  onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
                  placeholder="e.g., Terminal 1, Gate 15"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="edit-areaImage">Area Image</Label>
              <Input
                id="edit-areaImage"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setEditAreaImageFile(file);
                    setRemoveAreaImage(false);
                  }
                }}
                disabled={uploadingEditAreaImage}
              />
              {editAreaImageFile && (
                <p className="text-sm text-slate-500 mt-1">
                  Selected: {editAreaImageFile.name}
                </p>
              )}
              {editFormData.areaImageUrl && !editAreaImageFile && !removeAreaImage && (
                <div className="flex items-center justify-between mt-1">
                  <p className="text-sm text-slate-500">
                    Current: {editFormData.areaImageUrl.split('/').pop()}
                  </p>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => setRemoveAreaImage(true)}
                  >
                    Remove
                  </Button>
                </div>
              )}
              {removeAreaImage && (
                <p className="text-sm text-red-600 mt-1">
                  Image will be removed on save
                </p>
              )}
              {uploadingEditAreaImage && (
                <p className="text-sm text-blue-600 mt-1">
                  Uploading...
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="edit-renderImage">Render Image</Label>
              <Input
                id="edit-renderImage"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setEditRenderImageFile(file);
                    setRemoveRenderImage(false);
                  }
                }}
                disabled={uploadingEditRenderImage}
              />
              {editRenderImageFile && (
                <p className="text-sm text-slate-500 mt-1">
                  Selected: {editRenderImageFile.name}
                </p>
              )}
              {editFormData.renderImageUrl && !editRenderImageFile && !removeRenderImage && (
                <div className="flex items-center justify-between mt-1">
                  <p className="text-sm text-slate-500">
                    Current: {editFormData.renderImageUrl.split('/').pop()}
                  </p>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => setRemoveRenderImage(true)}
                  >
                    Remove
                  </Button>
                </div>
              )}
              {removeRenderImage && (
                <p className="text-sm text-red-600 mt-1">
                  Image will be removed on save
                </p>
              )}
              {uploadingEditRenderImage && (
                <p className="text-sm text-blue-600 mt-1">
                  Uploading...
                </p>
              )}
            </div>

            <div className="space-y-4 border-t pt-4 mt-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edit-requiresTenderProcess"
                  checked={editFormData.requiresTenderProcess}
                  onChange={(e) => setEditFormData({ ...editFormData, requiresTenderProcess: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="edit-requiresTenderProcess" className="text-sm font-medium">
                  Requires Tender Process (New Location)
                </Label>
              </div>

              {editFormData.requiresTenderProcess && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit-tenderType">Tender Type *</Label>
                      <Select
                        value={editFormData.tenderType || ''}
                        onValueChange={(value) => setEditFormData({ ...editFormData, tenderType: value as 'long-term' | 'short-term' })}
                        required={editFormData.requiresTenderProcess}
                      >
                        <SelectTrigger className={!editFormData.tenderType && editFormData.requiresTenderProcess ? 'border-red-500' : ''}>
                          <SelectValue placeholder="Select tender type..." />
                        </SelectTrigger>
                        <SelectContent className="z-[2000]">
                          <SelectItem value="long-term">Long-term</SelectItem>
                          <SelectItem value="short-term">Short-term</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-1">Required for machines to appear in Long-Term Listings</p>
                      {editFormData.requiresTenderProcess && !editFormData.tenderType && (
                        <p className="text-xs text-red-600 mt-1">Tender Type is required</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="edit-minimumMachines">Minimum Machines</Label>
                      <Input
                        id="edit-minimumMachines"
                        type="number"
                        min="1"
                        value={editFormData.minimumMachines || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, minimumMachines: e.target.value ? parseInt(e.target.value, 10) : '' })}
                        placeholder="e.g., 5"
                      />
                      <p className="text-xs text-slate-500 mt-1">Minimum machines required per venue (optional)</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit-tenderTimeframe">Timeframe</Label>
                      <Select
                        value={editFormData.tenderTimeframe || ''}
                        onValueChange={(value) => setEditFormData({ ...editFormData, tenderTimeframe: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select timeframe..." />
                        </SelectTrigger>
                        <SelectContent className="z-[2000]">
                          <SelectItem value="6 months">6 months</SelectItem>
                          <SelectItem value="12 months">12 months</SelectItem>
                          <SelectItem value="custom">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="edit-tenderLaunchDate">Launch Date</Label>
                      <Input
                        id="edit-tenderLaunchDate"
                        type="date"
                        value={editFormData.tenderLaunchDate || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, tenderLaunchDate: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Planogram</Label>
                    <Select
                      value={editFormData.planogramId || 'none'}
                      onValueChange={(value) => {
                        if (value === 'create-new') {
                          setEditingPlanogram('edit');
                          setShowPlanogramModal(true);
                        } else if (value === 'none') {
                          setEditFormData({ ...editFormData, planogramId: '', planogramConfig: '' });
                        } else {
                          setEditFormData({ ...editFormData, planogramId: value, planogramConfig: '' });
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select planogram..." />
                      </SelectTrigger>
                      <SelectContent className="z-[2000]">
                        <SelectItem value="none">None</SelectItem>
                        {availablePlanograms.map((planogram) => (
                          <SelectItem key={planogram.id} value={planogram.id}>
                            {planogram.name} {planogram.machineCount ? `(${planogram.machineCount} machines)` : ''}
                          </SelectItem>
                        ))}
                        <SelectItem value="create-new">+ Create New Planogram</SelectItem>
                      </SelectContent>
                    </Select>
                    {editFormData.planogramId && (
                      <p className="text-xs text-green-600 mt-1">✓ Planogram selected</p>
                    )}
                    {editFormData.planogramConfig && !editFormData.planogramId && (
                      <p className="text-xs text-green-600 mt-1">✓ Planogram configured</p>
                    )}
                  </div>
                  {editFormData.venueName && editFormData.tenderType === 'long-term' && (
                    <div className="space-y-2">
                      <Label>Machine Tier (for Long-Term Tender)</Label>
                      <Select
                        value={editFormData.machineTierId || 'none'}
                        onValueChange={async (value) => {
                          if (value === 'create-new') {
                            setShowCreateTierDialog(true);
                          } else if (value === 'none') {
                            setEditFormData({ ...editFormData, machineTierId: '' });
                          } else {
                            setEditFormData({ ...editFormData, machineTierId: value });
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select tier..." />
                        </SelectTrigger>
                        <SelectContent className="z-[2000]">
                          <SelectItem value="none">None</SelectItem>
                          {availableTiers.map((tier) => (
                            <SelectItem key={tier.id} value={tier.id}>
                              {tier.name} {tier.machineCount !== undefined ? `(${tier.machineCount} machines)` : ''}
                            </SelectItem>
                          ))}
                          <SelectItem value="create-new">+ Create New Tier</SelectItem>
                        </SelectContent>
                      </Select>
                      {editFormData.machineTierId && (
                        <p className="text-xs text-green-600 mt-1">✓ Tier selected</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="edit-isActive"
                checked={editFormData.isActive}
                onChange={(e) => setEditFormData({ ...editFormData, isActive: e.target.checked })}
              />
              <Label htmlFor="edit-isActive">Active</Label>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Tier Dialog */}
      <Dialog open={showCreateTierDialog} onOpenChange={setShowCreateTierDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Tier</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!newTierName.trim()) {
                alert('Tier name is required');
                return;
              }
              const venueName = showEditDialog ? editFormData.venueName : newMachine.venueName;
              if (!venueName) {
                alert('Venue name is required to create a tier');
                return;
              }
              try {
                const tier = await createMachineTier({
                  name: newTierName.trim(),
                  venueName: venueName,
                  planogramId: newTierPlanogramId || null,
                });
                await loadTiers(venueName);
                // Assign the newly created tier to the machine
                if (showEditDialog) {
                  setEditFormData({ ...editFormData, machineTierId: tier.id });
                } else {
                  setNewMachine({ ...newMachine, machineTierId: tier.id as any });
                }
                setShowCreateTierDialog(false);
                setNewTierName('');
                setNewTierPlanogramId('');
              } catch (error: any) {
                alert(error.message || 'Failed to create tier');
              }
            }}
          >
            <div className="space-y-4">
              <div>
                <Label htmlFor="tier-name">Tier Name</Label>
                <Input
                  id="tier-name"
                  value={newTierName}
                  onChange={(e) => setNewTierName(e.target.value)}
                  placeholder="e.g., Tier 1, Tier 2"
                  required
                />
              </div>
              <div>
                <Label>Planogram (Optional)</Label>
                <Select
                  value={newTierPlanogramId || 'none'}
                  onValueChange={(value) => {
                    setNewTierPlanogramId(value === 'none' ? '' : value);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select planogram..." />
                  </SelectTrigger>
                  <SelectContent className="z-[2000]">
                    <SelectItem value="none">None</SelectItem>
                    {availablePlanograms.map((planogram) => (
                      <SelectItem key={planogram.id} value={planogram.id}>
                        {planogram.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => {
                  setShowCreateTierDialog(false);
                  setNewTierName('');
                  setNewTierPlanogramId('');
                }}>
                  Cancel
                </Button>
                <Button type="submit">Create Tier</Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Machine</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>Are you sure you want to delete this machine?</p>
            {selectedMachine && (
              <div className="border rounded-lg p-4 bg-muted">
                <div className="flex items-center gap-2 mb-1">
                  {selectedMachine.pinColor && (
                    <div
                      className="w-4 h-4 rounded-full border border-gray-300"
                      style={{ backgroundColor: selectedMachine.pinColor }}
                    />
                  )}
                  <h4 className="font-semibold">{selectedMachine.name}</h4>
                  {selectedMachine.label && (
                    <span className="text-xs px-2 py-0.5 bg-background rounded-full">
                      {selectedMachine.label}
                    </span>
                  )}
                </div>
                {selectedMachine.description && (
                  <p className="text-sm text-muted-foreground mb-1">{selectedMachine.description}</p>
                )}
                <p className="text-sm">Cost: {formatCurrency(selectedMachine.costCents)}</p>
              </div>
            )}
            <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteConfirm}>
                Delete Machine
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Multiple Machines</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>Are you sure you want to delete {selectedMachines.length} machine{selectedMachines.length !== 1 ? 's' : ''}?</p>
            <div className="max-h-60 overflow-y-auto border rounded-lg p-3 bg-muted space-y-2">
              {selectedMachines.map((machine) => (
                <div key={machine.id} className="flex items-center gap-2 text-sm">
                  <div
                    className="w-3 h-3 rounded-full border border-gray-300"
                    style={{ backgroundColor: machine.pinColor || '#3B82F6' }}
                  />
                  <span className="font-medium">{machine.name}</span>
                  {machine.label && (
                    <span className="text-muted-foreground">({machine.label})</span>
                  )}
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowBulkDeleteDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleBulkDelete}>
                Delete {selectedMachines.length} Machine{selectedMachines.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={showBulkEditDialog} onOpenChange={setShowBulkEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Edit {selectedMachines.length} Machines</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Changes will be applied to all {selectedMachines.length} selected machines. Leave fields empty to keep existing values.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bulk-status">Status</Label>
                <Select
                  value={editFormData.status === '' ? '__keep__' : editFormData.status}
                  onValueChange={(value: 'Live/Available' | 'Reserved' | 'Coming Soon' | '__keep__') => {
                    const newStatus = value === '__keep__' ? '' : value;
                    setEditFormData({ ...editFormData, status: newStatus as 'Live/Available' | 'Reserved' | 'Coming Soon' | '' });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Keep existing" />
                  </SelectTrigger>
                  <SelectContent className="z-[2000]">
                    <SelectItem value="__keep__">Keep existing</SelectItem>
                    <SelectItem value="Live/Available">Live/Available</SelectItem>
                    <SelectItem value="Reserved">Reserved</SelectItem>
                    <SelectItem value="Coming Soon">Coming Soon</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="bulk-availabilityType">Availability Type</Label>
                <Select
                  value={editFormData.availabilityType === '' ? '__keep__' : editFormData.availabilityType}
                  onValueChange={(value: string) => {
                    const newAvailabilityType = value === '__keep__' ? '' : value;
                    setEditFormData({ ...editFormData, availabilityType: newAvailabilityType });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Keep existing" />
                  </SelectTrigger>
                  <SelectContent className="z-[2000]">
                    <SelectItem value="__keep__">Keep existing</SelectItem>
                    {availabilityTypeOptions.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bulk-venueType">Venue Type</Label>
                <CustomComboBox
                  fieldName="venueType"
                  value={editFormData.venueType}
                  onChange={(value) => setEditFormData({ ...editFormData, venueType: value })}
                  placeholder="Leave empty to keep existing"
                />
              </div>

              <div>
                <Label htmlFor="bulk-venueName">Venue Name</Label>
                <VenueNameComboBox
                  value={editFormData.venueName}
                  onChange={(value) => setEditFormData({ ...editFormData, venueName: value })}
                  venueType={editFormData.venueType}
                  placeholder="Leave empty to keep existing"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bulk-pinColor">Pin Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="bulk-pinColor"
                    type="color"
                    value={editFormData.pinColor}
                    onChange={(e) => setEditFormData({ ...editFormData, pinColor: e.target.value })}
                    className="w-20 h-10 p-1"
                  />
                  <Input
                    type="text"
                    value={editFormData.pinColor}
                    onChange={(e) => setEditFormData({ ...editFormData, pinColor: e.target.value })}
                    placeholder="#3B82F6"
                    className="flex-1"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 mt-8">
                <input
                  type="checkbox"
                  id="bulk-isActive"
                  checked={editFormData.isActive}
                  onChange={(e) => setEditFormData({ ...editFormData, isActive: e.target.checked })}
                />
                <Label htmlFor="bulk-isActive">Set all to Active</Label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bulk-planogram">Planogram</Label>
                <Select
                  value={editFormData.planogramId === '' ? '__keep__' : editFormData.planogramId || 'none'}
                  onValueChange={(value) => {
                    if (value === '__keep__') {
                      setEditFormData({ ...editFormData, planogramId: '' });
                    } else if (value === 'none') {
                      setEditFormData({ ...editFormData, planogramId: null as any });
                    } else if (value === 'create-new') {
                      setShowBulkCreatePlanogramDialog(true);
                    } else {
                      setEditFormData({ ...editFormData, planogramId: value });
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Keep existing" />
                  </SelectTrigger>
                  <SelectContent className="z-[2000]">
                    <SelectItem value="__keep__">Keep existing</SelectItem>
                    <SelectItem value="none">Remove planogram</SelectItem>
                    {availablePlanograms.map((planogram) => (
                      <SelectItem key={planogram.id} value={planogram.id}>
                        {planogram.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="create-new" className="text-blue-600 font-medium">
                      + Create New Planogram
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="bulk-tier">Machine Tier (Long-Term Tender)</Label>
                <Select
                  value={editFormData.machineTierId === '' ? '__keep__' : editFormData.machineTierId || 'none'}
                  onValueChange={(value) => {
                    if (value === '__keep__') {
                      setEditFormData({ ...editFormData, machineTierId: '' });
                    } else if (value === 'none') {
                      setEditFormData({ ...editFormData, machineTierId: null as any });
                    } else if (value === 'create-new') {
                      setShowBulkCreateTierDialog(true);
                    } else {
                      setEditFormData({ ...editFormData, machineTierId: value });
                    }
                  }}
                  disabled={availableTiers.length === 0 && selectedMachines.length > 0 && Array.from(new Set(selectedMachines.map(m => m.venueName).filter(Boolean))).length === 1}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={
                      selectedMachines.length > 0 && Array.from(new Set(selectedMachines.map(m => m.venueName).filter(Boolean))).length > 1
                        ? "Machines have different venues"
                        : "Keep existing"
                    } />
                  </SelectTrigger>
                  <SelectContent className="z-[2000]">
                    <SelectItem value="__keep__">Keep existing</SelectItem>
                    <SelectItem value="none">Remove tier</SelectItem>
                    {availableTiers.map((tier) => (
                      <SelectItem key={tier.id} value={tier.id}>
                        {tier.name} {tier.machineCount !== undefined ? `(${tier.machineCount} machines)` : ''}
                      </SelectItem>
                    ))}
                    <SelectItem value="create-new" className="text-blue-600 font-medium">
                      + Create New Tier
                    </SelectItem>
                  </SelectContent>
                </Select>
                {selectedMachines.length > 0 && Array.from(new Set(selectedMachines.map(m => m.venueName).filter(Boolean))).length > 1 && (
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠ Selected machines have different venue names. Tier assignment requires all machines to share the same venue.
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="bulk-requiresTenderProcess"
                  checked={editFormData.requiresTenderProcess}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setEditFormData({ 
                      ...editFormData, 
                      requiresTenderProcess: checked,
                      // Clear tenderType if unchecking
                      tenderType: checked ? editFormData.tenderType : '' as 'long-term' | 'short-term' | ''
                    });
                  }}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="bulk-requiresTenderProcess" className="text-sm font-medium">
                  Requires Tender Process (New Location)
                </Label>
              </div>

              {editFormData.requiresTenderProcess && (
                <div>
                  <Label htmlFor="bulk-tenderType">Tender Type *</Label>
                  <Select
                    value={editFormData.tenderType === '' ? '__keep__' : editFormData.tenderType || '__keep__'}
                    onValueChange={(value: string) => {
                      const newTenderType = value === '__keep__' ? '' : value;
                      setEditFormData({ 
                        ...editFormData, 
                        tenderType: newTenderType as 'long-term' | 'short-term' | '' 
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select tender type..." />
                    </SelectTrigger>
                    <SelectContent className="z-[2000]">
                      <SelectItem value="__keep__">Keep existing</SelectItem>
                      <SelectItem value="long-term">Long-term</SelectItem>
                      <SelectItem value="short-term">Short-term</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Required for machines to appear in Long-Term Listings</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowBulkEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={async () => {
                if (selectedMachines.length === 0) return;
                
                try {
                  // Build update object with only fields that have values
                  const updates: any = {
                    isActive: editFormData.isActive,
                  };
                  
                  if (editFormData.status) {
                    updates.status = editFormData.status;
                  }
                  if (editFormData.availabilityType) {
                    updates.availabilityType = editFormData.availabilityType;
                  }
                  if (editFormData.venueType) {
                    updates.venueType = editFormData.venueType;
                  }
                  if (editFormData.pinColor) {
                    updates.pinColor = editFormData.pinColor;
                  }
                  
                  // Handle venue name update first (before tier assignment)
                  if (editFormData.venueName) {
                    updates.venueName = editFormData.venueName;
                  }
                  
                  // Handle tender process fields
                  // Only update if checkbox was explicitly checked (true)
                  // If false, we don't include it (keeps existing value)
                  if (editFormData.requiresTenderProcess === true) {
                    updates.requiresTenderProcess = true;
                    
                    // Tender Type is required when enabling tender process
                    const tenderTypeValue = editFormData.tenderType;
                    if (!tenderTypeValue || (tenderTypeValue as string) === '') {
                      throw new Error('Tender Type is required when "Requires Tender Process" is enabled');
                    }
                    if (!editFormData.venueName || editFormData.venueName.trim() === '') {
                      throw new Error('Venue Name is required when "Requires Tender Process" is enabled');
                    }
                    updates.tenderType = tenderTypeValue;
                  } else if (editFormData.tenderType !== undefined && editFormData.tenderType !== '') {
                    // If only tender type is being updated (and requiresTenderProcess is already true on machines)
                    // Note: This assumes machines already have requiresTenderProcess=true
                    updates.tenderType = editFormData.tenderType;
                  }
                  
                  // Handle planogram assignment
                  // Only update if planogramId is explicitly set (not empty string which means "keep existing")
                  // But skip if we're also assigning a tier (tier assignment will handle planogram)
                  if (editFormData.planogramId !== undefined && editFormData.planogramId !== '' && 
                      !(editFormData.machineTierId && editFormData.machineTierId !== '' && editFormData.machineTierId !== 'none')) {
                    if (editFormData.planogramId === null || editFormData.planogramId === 'none') {
                      updates.planogramId = null;
                    } else {
                      updates.planogramId = editFormData.planogramId;
                    }
                    // Don't include planogramConfig in updates - backend will handle it
                    // Remove it if it was accidentally included
                    delete updates.planogramConfig;
                  }
                  
                  // Handle tier assignment
                  // Only update if machineTierId is explicitly set (not empty string which means "keep existing")
                  if (editFormData.machineTierId !== undefined && editFormData.machineTierId !== '') {
                    if (editFormData.machineTierId === null || editFormData.machineTierId === 'none') {
                      updates.machineTierId = null;
                    } else {
                      // Validate that all machines can be assigned to this tier
                      // Get the tier from availableTiers (already loaded)
                      const tier = availableTiers.find(t => t.id === editFormData.machineTierId);
                      if (!tier) {
                        throw new Error('Selected tier not found');
                      }
                      
                      // Check if all selected machines have the same venue name as the tier
                      const venueNames = Array.from(new Set(
                        selectedMachines.map(m => m.venueName).filter(Boolean)
                      ));
                      
                      if (venueNames.length > 1 || (venueNames.length === 1 && venueNames[0] !== tier.venueName)) {
                        throw new Error(`Cannot assign tier "${tier.name}" to machines with different venue names. All machines must have venue name "${tier.venueName}".`);
                      }
                      
                      updates.machineTierId = editFormData.machineTierId;
                      
                      // If tier has a planogram, assign it to all machines
                      // This ensures all machines in a tier have the same planogram
                      if (tier.planogramId) {
                        updates.planogramId = tier.planogramId;
                        // Don't include planogramConfig in updates - backend will handle it
                      }
                    }
                  }
                  
                  // Apply changes to all selected machines sequentially to avoid race conditions
                  // Especially important when assigning to tiers, as validation depends on other machines
                  const errors: string[] = [];
                  const successful: string[] = [];
                  
                  for (const machine of selectedMachines) {
                    try {
                      await updateMachine(machine.id, updates);
                      successful.push(machine.name || machine.id);
                    } catch (error: any) {
                      const errorMessage = error?.message || error?.toString() || 'Unknown error';
                      errors.push(`${machine.name || machine.id}: ${errorMessage}`);
                      console.error(`Failed to update machine ${machine.id}:`, error);
                    }
                  }
                  
                  if (errors.length > 0) {
                    const errorMsg = `Failed to update ${errors.length} machine(s):\n\n${errors.join('\n')}\n\n${successful.length > 0 ? `Successfully updated ${successful.length} machine(s).` : ''}`;
                    alert(errorMsg);
                    // Still reload machines to show partial updates
                    await loadMachines();
                    return;
                  }
                  
                  // Reset form
                  setEditFormData({
                    name: '',
                    description: '',
                    cost: '',
                    label: '',
                    pinColor: '#3B82F6',
                    isActive: true,
                    status: 'Live/Available' as 'Live/Available' | 'Reserved' | 'Coming Soon',
                    availabilityType: '',
                    availableSkus: '',
                    areaImageUrl: '',
                    renderImageUrl: '',
                    venueType: '',
                    venueName: '',
                    machineType: '',
                    resolution: '',
                    location: '',
                    capacity: '',
                    format: '',
                    screenSize: '',
                    playbackFormat: '',
                    duration: '',
                    orientation: '',
                    aspectRatio: '',
                    addressLine1: '',
                    addressLine2: '',
                    postCode: '',
                    county: '',
                    city: '',
                    requiresTenderProcess: false,
                    planogramConfig: '',
                    planogramId: '' as string,
                    tenderTimeframe: '',
                    tenderLaunchDate: '',
                    tenderType: '' as 'long-term' | 'short-term' | '',
                     minimumMachines: '' as string | number,
                     machineTierId: '' as string,
                     footfall: '' as string | number,
                     priceFromPer2Weeks: '',
                   });
                   
                   setShowBulkEditDialog(false);
                  setSelectedMachines([]);
                  await loadMachines();
                } catch (error: any) {
                  console.error('Failed to bulk update machines:', error);
                  const errorMessage = error?.message || error?.toString() || 'Failed to update some machines';
                  alert(`Failed to update machines: ${errorMessage}`);
                }
              }}>
                Apply to {selectedMachines.length} Machine{selectedMachines.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Create Tier Dialog */}
      <Dialog open={showBulkCreateTierDialog} onOpenChange={setShowBulkCreateTierDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Tier for Bulk Assignment</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!bulkTierName.trim()) {
                alert('Tier name is required');
                return;
              }
              
              // Get venue name from selected machines
              const venueNames = Array.from(new Set(
                selectedMachines.map(m => m.venueName).filter(Boolean)
              ));
              
              if (venueNames.length === 0) {
                alert('Selected machines must have a venue name');
                return;
              }
              
              if (venueNames.length > 1) {
                alert('All selected machines must have the same venue name to create a tier');
                return;
              }
              
              const venueName = venueNames[0];
              if (!venueName) {
                alert('Selected machines must have a venue name');
                return;
              }
              
              try {
                const tier = await createMachineTier({
                  name: bulkTierName.trim(),
                  venueName: venueName,
                  planogramId: bulkTierPlanogramId || null,
                });
                
                // Reload tiers for this venue
                await loadTiers(venueName);
                
                // Assign the newly created tier to all selected machines
                setEditFormData({ ...editFormData, machineTierId: tier.id });
                
                setShowBulkCreateTierDialog(false);
                setBulkTierName('');
                setBulkTierPlanogramId('');
              } catch (error: any) {
                alert(error.message || 'Failed to create tier');
              }
            }}
          >
            <div className="space-y-4">
              <div>
                <Label htmlFor="bulk-tier-name">Tier Name</Label>
                <Input
                  id="bulk-tier-name"
                  value={bulkTierName}
                  onChange={(e) => setBulkTierName(e.target.value)}
                  placeholder="e.g., Tier 1, Tier 2"
                  required
                />
              </div>
              <div>
                <Label>Planogram (Optional)</Label>
                <Select
                  value={bulkTierPlanogramId || 'none'}
                  onValueChange={(value) => {
                    setBulkTierPlanogramId(value === 'none' ? '' : value);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select planogram..." />
                  </SelectTrigger>
                  <SelectContent className="z-[2000]">
                    <SelectItem value="none">None</SelectItem>
                    {availablePlanograms.map((planogram) => (
                      <SelectItem key={planogram.id} value={planogram.id}>
                        {planogram.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="text-xs text-slate-500">
                This tier will be assigned to all {selectedMachines.length} selected machine{selectedMachines.length !== 1 ? 's' : ''} after creation.
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => {
                  setShowBulkCreateTierDialog(false);
                  setBulkTierName('');
                  setBulkTierPlanogramId('');
                }}>
                  Cancel
                </Button>
                <Button type="submit">Create Tier & Assign</Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Create Planogram Dialog */}
      <Dialog open={showBulkCreatePlanogramDialog} onOpenChange={setShowBulkCreatePlanogramDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Planogram for Bulk Assignment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              You'll configure the planogram grid after creating it. The planogram will then be assigned to all {selectedMachines.length} selected machine{selectedMachines.length !== 1 ? 's' : ''}.
            </p>
            <div>
              <Label htmlFor="bulk-planogram-name">Planogram Name</Label>
              <Input
                id="bulk-planogram-name"
                value={planogramName}
                onChange={(e) => setPlanogramName(e.target.value)}
                placeholder="e.g., Planogram 3"
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => {
                setShowBulkCreatePlanogramDialog(false);
                setPlanogramName('');
              }}>
                Cancel
              </Button>
              <Button onClick={async () => {
                if (!planogramName.trim()) {
                  alert('Planogram name is required');
                  return;
                }
                
                // Open planogram configuration modal
                setEditingPlanogram('create');
                setShowPlanogramModal(true);
                setShowBulkCreatePlanogramDialog(false);
                
                // Store that we're in bulk mode
                // We'll handle the assignment after planogram is created
              }}>
                Configure Planogram
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Planogram Configuration Dialog */}
      <Dialog open={showPlanogramModal} onOpenChange={(open) => {
        setShowPlanogramModal(open);
        if (!open) {
          setSelectedCells(new Set());
          setCategoryInput('');
          setPlanogramName('');
        }
      }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configure Planogram (6 Rows × 9 Columns)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Click on cells to select them, then enter a category name and click "Assign Category" to assign it to the selected cells.
            </p>

            {/* Planogram Name (only show when creating) */}
            {editingPlanogram === 'create' && (
              <div className="border-b pb-4">
                <Label htmlFor="planogramName" className="text-sm font-semibold">Planogram Name *</Label>
                <input
                  id="planogramName"
                  type="text"
                  value={planogramName}
                  onChange={(e) => setPlanogramName(e.target.value)}
                  placeholder="e.g., Planogram 3"
                  className="w-full px-3 py-2 border rounded-md mt-1"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">Enter a name for this planogram (required)</p>
              </div>
            )}

            {/* Category Assignment Controls */}
            <div className="flex gap-2 items-end border-b pb-4">
              <div className="flex-1">
                <Label htmlFor="categoryName">Category Name</Label>
                {!showNewCategoryInput ? (
                  <div className="flex gap-2 mt-1">
                    <Select
                      value={categoryInput}
                      onValueChange={(value) => {
                        if (value === '__add_new__') {
                          setShowNewCategoryInput(true);
                          setCategoryInput('');
                        } else {
                          setCategoryInput(value);
                          if (value && savedCategories[value]) {
                            setSelectedCategoryColor(savedCategories[value]);
                          }
                          setShowNewCategoryInput(false);
                        }
                      }}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select or add category..." />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(savedCategories).map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            <div className="flex items-center gap-2">
                              <div
                                className="w-4 h-4 rounded border border-gray-300"
                                style={{ backgroundColor: savedCategories[cat] }}
                              />
                              {cat}
                            </div>
                          </SelectItem>
                        ))}
                        <SelectItem value="__add_new__" className="text-blue-600 font-medium">
                          + Add New Category
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="categoryName"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="Enter new category name..."
                      className="flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newCategoryName.trim()) {
                          const trimmed = newCategoryName.trim();
                          if (!savedCategories[trimmed]) {
                            const updated = { ...savedCategories, [trimmed]: selectedCategoryColor };
                            setSavedCategories(updated);
                            localStorage.setItem('planogramCategories', JSON.stringify(updated));
                            setCategoryInput(trimmed);
                            setShowNewCategoryInput(false);
                            setNewCategoryName('');
                          } else {
                            // Category already exists, just select it
                            setCategoryInput(trimmed);
                            setSelectedCategoryColor(savedCategories[trimmed]);
                            setShowNewCategoryInput(false);
                            setNewCategoryName('');
                          }
                        } else if (e.key === 'Escape') {
                          setShowNewCategoryInput(false);
                          setNewCategoryName('');
                        }
                      }}
                      autoFocus
                    />
                    <input
                      type="color"
                      value={selectedCategoryColor}
                      onChange={(e) => setSelectedCategoryColor(e.target.value)}
                      className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                      title="Select color for category"
                    />
                    <Button
                      type="button"
                      onClick={() => {
                        if (newCategoryName.trim()) {
                          const trimmed = newCategoryName.trim();
                          if (!savedCategories[trimmed]) {
                            const updated = { ...savedCategories, [trimmed]: selectedCategoryColor };
                            setSavedCategories(updated);
                            localStorage.setItem('planogramCategories', JSON.stringify(updated));
                            setCategoryInput(trimmed);
                            setShowNewCategoryInput(false);
                            setNewCategoryName('');
                          } else {
                            setCategoryInput(trimmed);
                            setSelectedCategoryColor(savedCategories[trimmed]);
                            setShowNewCategoryInput(false);
                            setNewCategoryName('');
                          }
                        }
                      }}
                      disabled={!newCategoryName.trim()}
                    >
                      Add
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowNewCategoryInput(false);
                        setNewCategoryName('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
                {categoryInput && !showNewCategoryInput && (
                  <div className="flex items-center gap-2 mt-2">
                    <Label htmlFor="categoryColor" className="text-xs text-gray-600">
                      Color:
                    </Label>
                    <input
                      type="color"
                      id="categoryColor"
                      value={selectedCategoryColor}
                      onChange={(e) => {
                        const newColor = e.target.value;
                        setSelectedCategoryColor(newColor);
                        const updated = { ...savedCategories, [categoryInput]: newColor };
                        setSavedCategories(updated);
                        localStorage.setItem('planogramCategories', JSON.stringify(updated));
                      }}
                      className="w-12 h-8 rounded border border-gray-300 cursor-pointer"
                    />
                    <span className="text-xs text-gray-500">{selectedCategoryColor}</span>
                  </div>
                )}
              </div>
              <Button
                type="button"
                onClick={() => {
                  if (!categoryInput.trim() || selectedCells.size === 0) return;

                  const config = editingPlanogram === 'create' 
                    ? newMachine.planogramConfig 
                    : editFormData.planogramConfig;
                  
                  let positions: any[] = [];
                  if (config) {
                    try {
                      const parsed = JSON.parse(config);
                      positions = parsed.positions || [];
                    } catch (e) {
                      positions = [];
                    }
                  }

                  // Update or add positions for selected cells
                  selectedCells.forEach(key => {
                    const [row, col] = key.split(',').map(Number);
                    const existingIndex = positions.findIndex(p => p.row === row && p.col === col);
                    if (existingIndex >= 0) {
                      positions[existingIndex] = { row, col, category: categoryInput.trim() };
                    } else {
                      positions.push({ row, col, category: categoryInput.trim() });
                    }
                  });

                  const newConfig = JSON.stringify({
                    rows: 6,
                    columns: 9,
                    positions,
                  });

                  if (editingPlanogram === 'create') {
                    if (isBulkPlanogramMode) {
                      setEditFormData({ ...editFormData, planogramConfig: newConfig });
                    } else {
                      setNewMachine({ ...newMachine, planogramConfig: newConfig });
                    }
                  } else {
                    setEditFormData({ ...editFormData, planogramConfig: newConfig });
                  }

                  setSelectedCells(new Set());
                }}
                disabled={!categoryInput.trim() || selectedCells.size === 0}
              >
                Assign Category ({selectedCells.size} cells)
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowNewCategoryInput(true);
                  setCategoryInput('');
                }}
                disabled={showNewCategoryInput}
              >
                + New
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (selectedCells.size === 0) return;

                  const config = editingPlanogram === 'create' 
                    ? newMachine.planogramConfig 
                    : editFormData.planogramConfig;
                  
                  let positions: any[] = [];
                  if (config) {
                    try {
                      const parsed = JSON.parse(config);
                      positions = parsed.positions || [];
                    } catch (e) {
                      positions = [];
                    }
                  }

                  // Remove categories for selected cells
                  selectedCells.forEach(key => {
                    const [row, col] = key.split(',').map(Number);
                    positions = positions.filter(p => !(p.row === row && p.col === col));
                  });

                  const newConfig = JSON.stringify({
                    rows: 6,
                    columns: 9,
                    positions,
                  });

                  if (editingPlanogram === 'create') {
                    if (isBulkPlanogramMode) {
                      setEditFormData({ ...editFormData, planogramConfig: newConfig });
                    } else {
                      setNewMachine({ ...newMachine, planogramConfig: newConfig });
                    }
                  } else {
                    setEditFormData({ ...editFormData, planogramConfig: newConfig });
                  }

                  setSelectedCells(new Set());
                }}
                disabled={selectedCells.size === 0}
              >
                Clear Selected
              </Button>
            </div>

            {/* Planogram Grid */}
            <div className="border rounded-lg p-4 bg-gray-50 overflow-x-auto">
              <div className="min-w-[700px]">
                {Array.from({ length: 6 }, (_, row) => (
                  <div key={row} className="flex gap-2 mb-2">
                    {Array.from({ length: 9 }, (_, col) => {
                      const key = `${row},${col}`;
                      const isSelected = selectedCells.has(key);
                      
                      // Get category for this cell
                      const config = editingPlanogram === 'create' 
                        ? newMachine.planogramConfig 
                        : editFormData.planogramConfig;
                      let category = '';
                      if (config) {
                        try {
                          const parsed = JSON.parse(config);
                          const position = parsed.positions?.find((p: any) => p.row === row && p.col === col);
                          category = position?.category || '';
                        } catch (e) {
                          category = '';
                        }
                      }

                      const getCategoryColor = (cat: string) => {
                        if (!cat) return { bg: 'bg-white', border: 'border-gray-300' };
                        const savedColor = savedCategories[cat];
                        if (savedColor) {
                          // Convert hex to RGB for border (slightly darker)
                          const hex = savedColor.replace('#', '');
                          const r = parseInt(hex.substr(0, 2), 16);
                          const g = parseInt(hex.substr(2, 2), 16);
                          const b = parseInt(hex.substr(4, 2), 16);
                          const borderColor = `rgb(${Math.max(0, r - 30)}, ${Math.max(0, g - 30)}, ${Math.max(0, b - 30)})`;
                          return {
                            bg: savedColor,
                            border: borderColor,
                            style: { backgroundColor: savedColor, borderColor: borderColor }
                          };
                        }
                        return { bg: 'bg-purple-100', border: 'border-purple-300' };
                      };

                      return (
                        <button
                          key={col}
                          type="button"
                          onClick={() => {
                            const newSelected = new Set(selectedCells);
                            if (newSelected.has(key)) {
                              newSelected.delete(key);
                            } else {
                              newSelected.add(key);
                            }
                            setSelectedCells(newSelected);
                          }}
                          className={`
                            w-20 h-20 rounded border-2 text-xs font-medium flex flex-col items-center justify-center
                            transition-all cursor-pointer
                            ${isSelected ? 'ring-4 ring-blue-500 ring-offset-2' : ''}
                            ${category ? '' : 'bg-white border-gray-300 hover:border-blue-300'}
                          `}
                          style={category ? getCategoryColor(category).style : undefined}
                        >
                          <div className="text-[10px] text-gray-500 mb-1">[{row},{col}]</div>
                          {category && (
                            <div className="text-[9px] font-semibold text-center leading-tight px-1">
                              {category}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Legend */}
            <div className="text-xs text-muted-foreground">
              <strong>Tip:</strong> Click individual cells or click and drag to select multiple cells, then assign a category to them all at once.
            </div>

            <div className="flex justify-end gap-2 border-t pt-4">
              {editingPlanogram === 'create' && (
                <Button 
                  type="button" 
                  onClick={async () => {
                    // Save the planogram
                    const config = newMachine.planogramConfig;
                    
                    if (!config) {
                      alert('Please configure the planogram grid before saving.');
                      return;
                    }
                    
                    if (!planogramName.trim()) {
                      alert('Please enter a planogram name.');
                      return;
                    }
                    
                    try {
                      const savedPlanogram = await createPlanogram({
                        name: planogramName.trim(),
                        config: config,
                        description: null,
                      });
                      
                      // Set the planogramId on the machine
                      setNewMachine({ ...newMachine, planogramId: savedPlanogram.id, planogramConfig: '' });
                      
                      // Reload planograms list
                      await loadPlanograms();
                      
                      alert('Planogram saved successfully!');
                    } catch (error: any) {
                      console.error('Failed to save planogram:', error);
                      alert(error.message || 'Failed to save planogram. Please try again.');
                    }
                  }}
                  disabled={!newMachine.planogramConfig || !planogramName.trim()}
                >
                  Save Planogram
                </Button>
              )}
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowPlanogramModal(false);
                  setSelectedCells(new Set());
                  setCategoryInput('');
                  setPlanogramName('');
                  setIsBulkPlanogramMode(false);
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

